import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import '../textDisplay.css';
import { CustomEditor } from 'components/shared/CustomEditor';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import { TextDisplaySidebar } from 'components/shared/TextDisplaySidebar/TextDisplaySidebar';
import { ROLES } from 'utils/Constants';
import { removeStrongTags } from 'utils/StringUtils';
import { WordsProvider } from 'utils/contexts/ScoreBoldWordsContext';
import { SemanticScoreProvider } from 'utils/contexts/SemanticScoreContext';
import { TextDetailsIA } from 'components/shared/textdetailsIA/textdetailsIA';
import { PendingGenerationEmptyState } from '../../../components/shared/textdetailsIA/PendingGenerationEmptyState';
import CreateCustomAIAgent from '../CreateAgentIA';
import axiosPrivate from '../../../config/axiosPrivate';
import axiosSmartContent from '../../../config/axiosSmartContent';
import { CustomAutocomplete } from 'components/shared/DropDown/CustomAutocomplete';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import InsufficientCreditsModal from 'components/shared/modal/InsufficientCreditsModal';
import SmartContentPopin from 'components/shared/SmartcontentModal/SmartContentPopin';
import TranslateContentPopin from 'components/shared/SmartcontentModal/TranslateContentPopin';
import AssignTextToProjectPopin from 'components/shared/SmartcontentModal/AssignTextToProjectPopin';
import SmartTextTranslationView from 'components/shared/SmartcontentModal/SmartTextTranslationView';
import TranslationSavedModal from 'components/shared/modal/TranslationSavedModal';
import TextAssignedToProjectModal from 'components/shared/modal/TextAssignedToProjectModal';
import 'components/shared/SmartcontentModal/SmartContentPopintrad.css';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import WordPressDeployModal from 'components/shared/modal/WordPressDeployModal';
import { isTextRevisionNotAllowedError } from 'utils/hooks/useTextStatusChange';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { UserSessionSelector } from 'store/UserSession';
import { useAdminClientAutoSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientAutoSave';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import LLMSelector from "../LLMSelector";
import {
  saveFormSnapshot,
  clearFormSnapshot,
  smartTextFormSelector,
} from 'store/AdminClient/SmartTextForm';
import { aiSmartContentTypeSelector } from 'store/AdminClient/AISmartContentType';
import {
  transformToSemanticGap,
  extractKeywordsWithFrequency,
  formatKeywordData,
  extractStrKeywordsWithFrequency,
  transformToKeywordFrequencyMap,
  extractSemanticGapFromFullDetails,
  normalizeSemanticGapList,
} from 'helpers/FormatKeywordsHelper';
import {
  buildUnassignedGenerationPayload,
  resolveTypeGeneration,
  runUnassignedGenerationJob,
  llmIdToGenerationApiValue,
  fetchUnassignedGenerationStatus,
  watchUnassignedGenerationUntilDone,
  isGenerationInProgress,
} from 'utils/smartContent/smartContentGenerationUtils';
import { takePendingKnowledgeBaseFiles } from 'utils/smartContent/smartContentPopinFilesStore';
import { isPaymentRequiredError } from 'utils/smartContent/creditConsumption';
import usePageTracking from 'utils/hooks/usePageTracking';
import { NAVIGATING_TO_TRANSLATION_IA_KEY } from 'utils/smartContent/useSmartTextTranslation';
import { useAssignTextToProject } from 'utils/smartContent/useAssignTextToProject';
import { resolveLanguageDisplayLabel } from 'utils/smartContent/translationLanguageUtils';
import { navigateBackOrDashboard } from 'utils/navigation/safeBackNavigation';

const ADMIN_CLIENT_CREATE_IA_SMART_TEXT_PAGE = '/AdminClient/createIAsmarttext';
const ADMIN_CLIENT_DASHBOARD = '/AdminClient/AdminMainMenu';

const SMART_POPIN_CONTEXT_KEY = 'SmartContentPopinContext';
const BRIEF_FLOW_RETURN_KEY = 'AdminClient_FromBriefFlow';
const BRIEF_TEXT_ID_SESSION_KEY = 'AdminClient_BriefTextId';
const RETURNING_TO_BRIEF_KEY = 'AdminClient_ReturningToBrief';
const SETTINGS_POPIN_DRAFT_KEY = 'SmartContentSettingsPopinDraft';
const SETTINGS_POPIN_OPEN_KEY = 'SmartContentSettingsPopinOpen';
const GENERATION_ERROR_MESSAGE = "Une erreur est survenue lors de la génération. Veuillez réessayer. Si le problème persiste, contactez notre support.";

const isGenerationLoadingContent = (html) => {
  if (!html || typeof html !== 'string') return false;
  return html.includes('gifencours.gif') || html.includes('Rédaction en cours');
};

const isTranslationLoadingContent = (html) => {
  if (!html || typeof html !== 'string') return false;
  return html.includes('gifencours.gif') || html.includes('Traduction en cours');
};

const buildTranslationLoadingHtml = (loadingLabel) => `
  <div style="text-align: center; padding: 40px;">
    <img src="/Images/gifencours.gif" alt="${loadingLabel}" style="width: 200px; height: 200px; margin-bottom: -30px;" />
    <p style="color: #666; font-size: 16px; font-weight: 500;">${loadingLabel}</p>
  </div>
`;

const buildGenerationLoadingHtml = (redactionLabel, generationLabel) => `
  <div style="text-align: center; padding: 40px;">
    <img src="/Images/gifencours.gif" alt="${generationLabel}" style="width: 200px; height: 200px; margin-bottom: 20px;" />
    <p style="color: #666; font-size: 16px; font-weight: 500;">${redactionLabel}</p>
  </div>
`;

const STREAMING_CURSOR_REGEX = /<span style="display:inline-block;width:2px[\s\S]*?<\/span>/gi;

const stripStreamingCursor = (html) =>
  String(html || '').replace(STREAMING_CURSOR_REGEX, '');

const consumeSettingsPopinRestoreSession = () => {
  try {
    const shouldOpen = sessionStorage.getItem(SETTINGS_POPIN_OPEN_KEY) === 'true';
    if (!shouldOpen) {
      return { open: false, draft: null };
    }

    sessionStorage.removeItem(SETTINGS_POPIN_OPEN_KEY);
    sessionStorage.removeItem('isNavigatingToAgentEdit');

    const raw = sessionStorage.getItem(SETTINGS_POPIN_DRAFT_KEY);
    sessionStorage.removeItem(SETTINGS_POPIN_DRAFT_KEY);

    if (!raw) {
      return { open: true, draft: null };
    }

    const parsed = JSON.parse(raw);
    const maxAge = 30 * 60 * 1000;
    if (!parsed?.form || Date.now() - (parsed.timestamp || 0) >= maxAge) {
      return { open: true, draft: null };
    }

    return { open: true, draft: parsed.form };
  } catch {
    return { open: false, draft: null };
  }
};

const YOUTUBE_API_ENDPOINT =
  '/smart-content/smart_content/youtube_description';

const buildYoutubeApiUrl = () => {
  const params = new URLSearchParams();
  params.set('reasoning', 'LOW');
  params.set('activeWebSearch', 'true');
  return `${YOUTUBE_API_ENDPOINT}?${params.toString()}`;
};

const ENRICHMENT_LLM_OPTIONS = [
  { id: 'claude', name: 'Claude' },
  { id: 'gemini', name: 'Gemini' },
];
const normalizeLlmId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'both' || normalized === 'claude+gpt' || normalized === 'gpt+claude') {
    return 'claude_gpt';
  }
  return normalized.replace(/\s+/g, '_');
};

const formatModelName = (id) => {
  if (id === 'gpt') return 'GPT';
  if (id === 'claude') return 'Claude';
  if (id === 'claude_gpt') return 'Claude + GPT';
  if (id === 'perplexity') return 'Perplexity';
  if (id === 'gemini') return 'Gemini';
  if (id === 'premium') return 'premium';
  return String(id || '').toUpperCase();
};

const getLlmIdFromAgentModel = (model) => {
  if (model == null || !String(model).trim()) return '';
  const modelLower = String(model).trim().toLowerCase();
  if (modelLower.includes('gpt') && modelLower.includes('claude')) return 'claude_gpt';
  if (modelLower.includes('premium')) return 'premium';
  if (modelLower.includes('perplexity')) return 'perplexity';
  if (modelLower.includes('gemini')) return 'gemini';
  if (modelLower.includes('claude')) return 'claude';
  if (modelLower.includes('gpt')) return 'gpt';
  return '';
};

const getAgentSettingsFlagsFromAgentModel = (model) => {
  const llmId = getLlmIdFromAgentModel(model);
  if (!llmId) {
    return {
      useGpt: false,
      useClaude: false,
      useBoth: false,
      usePerplexity: false,
      useGemini: false,
      usePremium: false,
    };
  }
  return getAgentSettingsFlagsFromLlm(llmId);
};

const ENRICHMENT_ALLOWED_LLMS = new Set(['claude', 'gemini']);

const getAgentSettingsFlagsFromLlm = (llm, { isContentImprovement = false } = {}) => {
  let normalized = normalizeLlmId(llm) || (isContentImprovement ? 'claude' : 'gpt');
  if (isContentImprovement && !ENRICHMENT_ALLOWED_LLMS.has(normalized)) {
    normalized = 'claude';
  }
  return {
    useGpt: normalized === 'gpt',
    useClaude: normalized === 'claude',
    useBoth: normalized === 'claude_gpt',
    usePerplexity: normalized === 'perplexity',
    useGemini: normalized === 'gemini',
    usePremium: normalized === 'premium',
  };
};

const getUserIaConfigsFromLocalStorage = () => {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) return [];
    const parsed = JSON.parse(rawUser);
    const iaConfigs = parsed?.user?.iaConfigs || parsed?.iaConfigs;
    return Array.isArray(iaConfigs) ? iaConfigs : [];
  } catch {
    return [];
  }
};

const getActiveTokenForProvider = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  if (!providerKey) return '';

  const iaConfigs = getUserIaConfigsFromLocalStorage();
  const item = iaConfigs.find(
    (c) =>
      c?.active === true &&
      String(c?.iaProvider || '').toLowerCase() === providerKey &&
      typeof c?.token === 'string'
  );

  return item?.token?.trim?.() || '';
};

const DEFAULT_MODELS_BY_PROVIDER = {
  openai: 'GPT_4_1',
  claude: 'CLAUDE_SONNET_4_6',
  perplexity: 'SONAR',
  gemini: 'GEMINI_2_5_PRO',
};

const normalizeModelForApi = (model) => {
  if (!model) return '';
  return String(model)
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
};

const getActiveIaConfigForProvider = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  if (!providerKey) return null;
  const iaConfigs = getUserIaConfigsFromLocalStorage();
  return (
    iaConfigs.find(
      (c) =>
        c?.active === true &&
        String(c?.iaProvider || '').toLowerCase() === providerKey
    ) || null
  );
};

const getModelForProviderInApi = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  const activeConfig = getActiveIaConfigForProvider(providerKey);
  const activeModel = normalizeModelForApi(activeConfig?.iaModel);
  if (activeModel) return activeModel;
  if (providerKey === 'openai') return DEFAULT_MODELS_BY_PROVIDER.openai;
  return DEFAULT_MODELS_BY_PROVIDER[providerKey] || '';
};

const GPT_STREAM_ENDPOINT =
  '/smart-content/smart_content/redaction/generate_text/gpt/stream';

const buildGptStreamUrl = () => {
  const params = new URLSearchParams();
  params.set('maxTokens', '10000');
  params.set('temperature', '0.3');
  params.set('chatGPTModel', 'GPT_5_6');
  params.set('reasoning', 'MEDIUM');
  params.set('activeWebSearch', 'true');
  return `${GPT_STREAM_ENDPOINT}?${params.toString()}`;
};

const buildIaHeadersForRedactionText = (selectedLlm) => {
  const llm = String(selectedLlm || '').toLowerCase();
  const headers = {};

  const pushHeader = (iaProvider, headerName) => {
    const token = getActiveTokenForProvider(iaProvider);
    if (token) headers[headerName] = token;
  };

  if (llm === 'gpt') {
    pushHeader('openai', 'X-OpenAI-Api-Key');
  } else if (llm === 'claude') {
    pushHeader('claude', 'X-Anthropic-Api-Key');
  } else if (llm === 'perplexity') {
    pushHeader('perplexity', 'X-Perplexity-Api-Key');
  } else if (llm === 'gemini') {
    pushHeader('gemini', 'X-Gemini-Api-Key');
  } else if (llm === 'both') {
    pushHeader('openai', 'X-OpenAI-Api-Key');
    pushHeader('claude', 'X-Anthropic-Api-Key');
  }

  return headers;
};

const buildIaHeadersForTitleMeta = (selectedLlm) => {

  const llm = String(selectedLlm || '').toLowerCase();
  const headers = {};

  if (llm === 'claude') {
    const token = getActiveTokenForProvider('claude');
    if (token) headers['X-Anthropic-Api-Key'] = token;
  } else {
    const token = getActiveTokenForProvider('openai');
    if (token) headers['X-OpenAI-Api-Key'] = token;
  }

  return headers;
};

const formatGeneratedTextToHtml = (generatedText) => {
  if (!generatedText || generatedText.trim() === '') return '';
  if (generatedText.includes('<')) return generatedText;
  return `<p>${generatedText.replace(/\n/g, '</p><p>')}</p>`;
};

// Configuration centralisée par type de contenu
const CONTENT_TYPE_CONFIG = {
  PRODUCT: {
    service: 'PRODUCT',
    contentType: 'PRODUCT',
    agentType: 'PRODUCT',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  LONG_CATEGORY: {
    service: 'LONG_CATEGORY',
    contentType: 'LONG_CATEGORY',
    agentType: 'LONG_CATEGORY',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  STORE_LOCATOR: {
    service: 'STORE_LOCATOR',
    contentType: 'STORE_LOCATOR',
    agentType: 'STORE_LOCATOR',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  GMB: {
    service: 'GMB',
    contentType: 'GMB',
    agentType: 'GMB',
    showSeoControls: false,
    showUrlControls: true,
    showScore: false,
  },
  CONTENT_IMPROVEMENT: {
    service: 'CONTENT_IMPROVEMENT',
    contentType: 'CONTENT_IMPROVEMENT',
    agentType: 'CONTENT_IMPROVEMENT',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  OTHER: {
    service: 'OTHER',
    contentType: 'OTHER',
    agentType: 'OTHER',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  YOUTUBE: {
    service: 'YOUTUBE',
    contentType: 'YOUTUBE',
    agentType: 'YOUTUBE',
    showSeoControls: false,
    showUrlControls: false,
    showScore: false,
  },
};

export default function CreateIASmartText() {
  const history = useHistory();
  const location = useLocation();
  const isConsultantRoute = location.pathname.startsWith('/Consultant');
  const createPagePath = isConsultantRoute
    ? '/Consultant/createIAsmarttext'
    : ADMIN_CLIENT_CREATE_IA_SMART_TEXT_PAGE;
  const homePath = isConsultantRoute ? '/Consultant/MainMenu' : ADMIN_CLIENT_DASHBOARD;
  const projectsPath = isConsultantRoute
    ? '/Consultant/ProjectsEnCours'
    : '/AdminClient/ProjectsEnCours';
  const agentEditPath = '/AdminClient/createagentiafromlist';
  usePageTracking(createPagePath);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: textId } = useParams();

  // Déterminer le type de contenu courant (Redux en priorité, fallback querystring)
  const searchParams = new URLSearchParams(location.search || '');
  const typeFromQuery =
    searchParams.get('contentType') ||
    searchParams.get('typeContenu') ||
    searchParams.get('type') ||
    '';
  const typeFromStore = useSelector(aiSmartContentTypeSelector);
  const resolvedType =
    (typeFromStore && CONTENT_TYPE_CONFIG[typeFromStore] && typeFromStore) ||
    (typeFromQuery && CONTENT_TYPE_CONFIG[typeFromQuery] && typeFromQuery) ||
    'PRODUCT';

  const initialContentType = resolvedType;
  const [currentContentType] = useState(initialContentType);
  const contentConfig = CONTENT_TYPE_CONFIG[currentContentType];

  // Récupérer l'état Redux
  const reduxFormState = useSelector(smartTextFormSelector);
  const { user } = useSelector(UserSessionSelector);

  const [expanded, setExpanded] = useState(true);
  const [currentTextId, setCurrentTextId] = useState(textId ? String(textId) : null);


  const resolveActualTextId = useCallback((fallbackId = null) => {
    const fromUrl = textId ? String(textId) : null;
    const fromStorage = localStorage.getItem('AdminClient_TextId');
    return fromUrl || fromStorage || (fallbackId != null ? String(fallbackId) : null);
  }, [textId]);

  const syncTextIdStorageAndUrl = useCallback((id) => {
    if (!id) return;
    const idStr = String(id);
    try {
      localStorage.setItem('AdminClient_TextId', idStr);
    } catch { }
    setCurrentTextId(idStr);
    const targetPath = `${createPagePath}/${idStr}`;
    if (location.pathname !== targetPath) {
      history.replace(`${targetPath}${location.search || ''}`);
    }
  }, [history, location.pathname, location.search, createPagePath]);

  useEffect(() => {
    if (!textId) return;
    try {
      localStorage.setItem('AdminClient_TextId', String(textId));
    } catch { }
    setCurrentTextId(String(textId));
  }, [textId]);

  const [formData, setFormData] = useState({
    keyword: '',
    url: '',
    titleTag: '',
    metaDescriptionTag: '',
  });

  const [errors, setErrors] = useState({
    titleError: false,
    metaDescriptionError: false,
  });

  const [agentSettings, setAgentSettings] = useState({
    customAgent: '',
    wordsPerText: 0,
    useGpt: false,
    useClaude: false,
    useBoth: false,
    usePerplexity: false,
    useGemini: false,
    usePremium: false,
  });

  const hasAutoGeneratedFromPopin = useRef(false);
  const popinAutoGenContextRef = useRef(null);
  const [shouldAutoStartFromPopin, setShouldAutoStartFromPopin] = useState(false);
  const [isStreamingMode, setIsStreamingMode] = useState(false);
  const streamingDivRef = useRef(null);
  const lastValidEditorContentRef = useRef('');
  const lastValidTitleRef = useRef('');
  const lastValidMetaRef = useRef('');

  const handleAgentChange = (name) => (e) => {
    const value = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    if (name === 'useGpt' || name === 'useClaude' || name === 'useBoth' || name === 'usePerplexity' || name === 'useGemini' || name === 'usePremium') {
      if (name === 'useBoth') {
        setAgentSettings({ ...agentSettings, useBoth: value, useGpt: false, useClaude: false, usePerplexity: false, useGemini: false, usePremium: false });
      } else if (name === 'useGpt') {
        setAgentSettings({ ...agentSettings, useGpt: value, useClaude: false, useBoth: false, usePerplexity: false, useGemini: false, usePremium: false });
      } else if (name === 'useClaude') {
        setAgentSettings({ ...agentSettings, useClaude: value, useGpt: false, useBoth: false, usePerplexity: false, useGemini: false, usePremium: false });
      } else if (name === 'usePerplexity') {
        setAgentSettings({ ...agentSettings, usePerplexity: value, useGpt: false, useClaude: false, useBoth: false, useGemini: false, usePremium: false });
      } else if (name === 'useGemini') {
        setAgentSettings({ ...agentSettings, useGemini: value, useGpt: false, useClaude: false, useBoth: false, usePerplexity: false, usePremium: false });
      } else if (name === 'usePremium') {
        setAgentSettings({ ...agentSettings, usePremium: value, useGpt: false, useClaude: false, useBoth: false, usePerplexity: false, useGemini: false });
      }
      return;
    }
    setAgentSettings((prev) => ({ ...prev, [name]: value }));
  };

  const isFieldDisabled = () => isConsultantRoute;

  const handleChange = (name) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (name) => () => {
    if (name === 'titleTag') {
      setErrors((prev) => ({ ...prev, titleError: !formData.titleTag.trim() }));
    }
    if (name === 'metaDescriptionTag') {
      setErrors((prev) => ({ ...prev, metaDescriptionError: !formData.metaDescriptionTag.trim() }));
    }
  };

  // Fonction pour gérer le changement de keywords
  const handleKeywordChange = (e) => {
    const newValue = e.target.value;
    setCombinedKeywords(newValue);
    setKeywords(extractStrKeywordsWithFrequency(newValue));

    const newGap = transformToSemanticGap(newValue);
    setList(newGap);
  };

  const applySemanticGapState = useCallback((semanticGapInput) => {
    if (Array.isArray(semanticGapInput) && semanticGapInput.length > 0) {
      setList(semanticGapInput);
      setKeywords(extractKeywordsWithFrequency(semanticGapInput));
      setCombinedKeywords(formatKeywordData(semanticGapInput));
      return;
    }

    if (typeof semanticGapInput === 'string' && semanticGapInput.trim()) {
      setCombinedKeywords(semanticGapInput);
      setKeywords(extractStrKeywordsWithFrequency(semanticGapInput));
      setList(transformToSemanticGap(semanticGapInput));
    }
  }, []);

  // Fonction pour gérer la mise à jour du gap
  const handleGapUpdate = (newGap) => {
    applySemanticGapState(newGap);
  };

  const getCurrentLlmForPopin = () => {
    const fromDb = getLlmIdFromAgentModel(aiAgentModel);
    if (fromDb) return fromDb;

    if (agentSettings.useBoth) return 'claude_gpt';
    if (agentSettings.useClaude) return 'claude';
    if (agentSettings.usePerplexity) return 'perplexity';
    if (agentSettings.useGemini) return 'gemini';
    if (agentSettings.usePremium) return 'premium';
    if (agentSettings.useGpt) return 'gpt';
    if (contentConfig?.service === 'CONTENT_IMPROVEMENT') return 'claude';
    return '';
  };

  const getCurrentPresetForPopin = () => {
    const words = Number(agentSettings.wordsPerText) || 0;
    if (words === 300) return { preset: 'ARTICLE_COURT_300', presetWordCount: '' };
    if (words === 600) return { preset: 'ARTICLE_MOYEN_600', presetWordCount: '' };
    if (words === 1000) return { preset: 'ARTICLE_LONG_1000', presetWordCount: '' };
    if (words === 350) return { preset: 'YOUTUBE_350', presetWordCount: '' };
    if (words === 200) return { preset: 'LINKEDIN_200', presetWordCount: '' };
    if (words === 150) return { preset: 'X_150', presetWordCount: '' };
    if (!words) return { preset: '', presetWordCount: '' };
    return { preset: 'CUSTOM', presetWordCount: String(words) };
  };

  const { keyword, url, titleTag, metaDescriptionTag } = formData;
  const { titleError, metaDescriptionError } = errors;
  const [editorContent, setEditorContent] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [headerActionsOpen, setHeaderActionsOpen] = useState(false);
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);
  const [isTextDataLoaded, setIsTextDataLoaded] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(424);
  const [websites, setWebsites] = useState('');
  const [writingAxis, setWritingAxis] = useState('');
  const [existingText, setExistingText] = useState('');
  const [textKnowledgeBaseFiles, setTextKnowledgeBaseFiles] = useState(null);
  const [retranscription, setRetranscription] = useState('');
  const [initialHtmlContent, setInitialHtmlContent] = useState('');
  const [initialTextContent, setInitialTextContent] = useState('');
  const [initialTextWordCount, setInitialTextWordCount] = useState(0);
  const [createIA, setCreateIA] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [websitesList, setWebsitesList] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [redactionAgentsList, setRedactionAgentsList] = useState([]);
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [settingsPopinRestore] = useState(() => consumeSettingsPopinRestoreSession());
  const [isSettingsPopinOpen, setIsSettingsPopinOpen] = useState(settingsPopinRestore.open);
  const [isTranslatePopinOpen, setIsTranslatePopinOpen] = useState(false);
  const [isTranslationMode, setIsTranslationMode] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState('');
  const [translationTargetLanguage, setTranslationTargetLanguage] = useState(null);
  const [translationAgentId, setTranslationAgentId] = useState('');
  const [isTranslationSaveModalOpen, setIsTranslationSaveModalOpen] = useState(false);
  const [translationSaveModalMode, setTranslationSaveModalMode] = useState('create');
  const [isSavingTranslation, setIsSavingTranslation] = useState(false);
  const [savedTranslationTextId, setSavedTranslationTextId] = useState(null);
  const savedTranslationTextIdRef = useRef(null);
  const [popinRestoreDraft, setPopinRestoreDraft] = useState(settingsPopinRestore.draft);
  const [aiAgentModel, setAiAgent] = useState('');
  const [availableAiModels, setAvailableAiModels] = useState([]);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [isCreditsResolved, setIsCreditsResolved] = useState(false);
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = useState(null);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [showSaveBeforeDownloadModal, setShowSaveBeforeDownloadModal] = useState(false);
  const [isRevisionErrorModalOpen, setIsRevisionErrorModalOpen] = useState(false);
  const [isSaveErrorModalOpen, setIsSaveErrorModalOpen] = useState(false);

  // États pour la gestion du gap sémantique
  const [list, setList] = useState([]);
  const [keywords, setKeywords] = useState('');
  const [combinedKeywords, setCombinedKeywords] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isDelivering, setIsDelivering] = useState(false);
  const [deliverError, setDeliverError] = useState('');
  const [deliverSuccess, setDeliverSuccess] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validateError, setValidateError] = useState('');
  const [validateSuccess, setValidateSuccess] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isGoingOnline, setIsGoingOnline] = useState(false);
  const [deployError, setDeployError] = useState('');
  const [deploySuccess, setDeploySuccess] = useState(false);
  const [showWordPressDeployModal, setShowWordPressDeployModal] = useState(false);
  const [wordPressDeployContent, setWordPressDeployContent] = useState('');
  const [isSendingToRevision, setIsSendingToRevision] = useState(false);
  const [revisionError, setRevisionError] = useState('');
  const [revisionSuccess, setRevisionSuccess] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const [currentContentScore, setCurrentContentScore] = useState(null);
  const [isRegeneratingTitle, setIsRegeneratingTitle] = useState(false);
  const [isRegeneratingMeta, setIsRegeneratingMeta] = useState(false);

  const { autoSave } = useAdminClientAutoSave({ textId: currentTextId });
  const lastContentScoreRef = useRef(null);

  const getPersistedContentScore = useCallback((baseScore = currentContentScore) => {
    const semanticGapValue =
      list && list.length > 0
        ? list
        : (combinedKeywords ? transformToSemanticGap(combinedKeywords) : []);

    if (!semanticGapValue.length) {
      return baseScore;
    }

    if (!baseScore) {
      return {
        semanticResult: {
          data: {
            semanticGap: semanticGapValue,
            remainingKeywords: [],
            wordsInBold: [],
          },
        },
      };
    }

    return {
      ...baseScore,
      semanticResult: {
        ...(baseScore.semanticResult || {}),
        data: {
          ...(baseScore.semanticResult?.data || {}),
          semanticGap: semanticGapValue,
        },
      },
    };
  }, [currentContentScore, list, combinedKeywords]);

  const resolveEditorContentForSave = useCallback(() => {
    if (isStreamingMode && streamingDivRef.current?.innerHTML) {
      const streamingHtml = stripStreamingCursor(streamingDivRef.current.innerHTML);
      if (streamingHtml.trim() && !isGenerationLoadingContent(streamingHtml)) {
        return streamingHtml;
      }
    }

    if (editorContent && !isGenerationLoadingContent(editorContent)) {
      return editorContent;
    }

    return lastValidEditorContentRef.current || '';
  }, [isStreamingMode, editorContent]);

  const buildAutoSaveRedactionData = useCallback((actualTextId) => {
    return {
      id: actualTextId,
      numberWords: Number(agentSettings.wordsPerText) || 0,
      semanticGap: [],
      axeRedaction: writingAxis || '',
      retranscription: String(retranscription || '').trim(),
      internalLinking: websites || '',
    };
  }, [
    agentSettings.wordsPerText,
    writingAxis,
    retranscription,
    websites,
  ]);

  const performAutoSave = useCallback(async (contentScoreOverride) => {
    if (isConsultantRoute) return;

    const actualTextId = resolveActualTextId(currentTextId);

    if (!actualTextId) return;

    const resolvedEditorContent = resolveEditorContentForSave();
    if (resolvedEditorContent && !isGenerationLoadingContent(resolvedEditorContent)) {
      lastValidEditorContentRef.current = resolvedEditorContent;
    }

    const resolvedTitle = String(titleTag || '').trim() || lastValidTitleRef.current;
    const resolvedMeta = String(metaDescriptionTag || '').trim() || lastValidMetaRef.current;
    const resolvedContentScore =
      contentScoreOverride ?? currentContentScore ?? lastContentScoreRef.current;

    const isYoutube = contentConfig?.service === 'YOUTUBE';
    const resolvedInitialText =
      String(initialTextContent || '').trim()
      || getTextFromHtml(initialHtmlContent).trim()
      || String(existingText || '').trim();
    const resolvedInitialHtml =
      String(initialHtmlContent || '').trim()
      || (resolvedInitialText ? plainTextToHtml(resolvedInitialText) : '');

    await autoSave(
      getTextFromHtml(resolvedEditorContent),
      resolvedEditorContent,
      buildAutoSaveRedactionData(actualTextId),
      isYoutube && resolvedInitialHtml ? resolvedInitialHtml : '',
      isYoutube && resolvedInitialText ? resolvedInitialText : '',
      'SMART',
      url,
      resolvedTitle,
      resolvedMeta,
      getPersistedContentScore(resolvedContentScore)
    );
  }, [
    isConsultantRoute,
    autoSave,
    buildAutoSaveRedactionData,
    resolveEditorContentForSave,
    resolveActualTextId,
    currentTextId,
    contentConfig,
    initialTextContent,
    initialHtmlContent,
    existingText,
    url,
    titleTag,
    metaDescriptionTag,
    currentContentScore,
    getPersistedContentScore,
  ]);

  const handleScoreDataChange = useCallback(
    async (scoreData) => {
      setCurrentContentScore(scoreData);

      const previousScore = lastContentScoreRef.current;
      lastContentScoreRef.current = scoreData;

      if (isConsultantRoute) {
        return;
      }

      const prevGlobal = previousScore?.globalScore ?? null;
      const nextGlobal = scoreData?.globalScore ?? null;

      const actualTextId = resolveActualTextId(currentTextId);

      if (!actualTextId || nextGlobal == null) {
        return;
      }

      if (prevGlobal !== null && prevGlobal === nextGlobal) {
        return;
      }

      try {
        await performAutoSave(scoreData);
      } catch (e) {
        console.error('❌ Auto-save après mise à jour du contentScore a échoué:', e);
      }
    },
    [performAutoSave, resolveActualTextId, currentTextId, isConsultantRoute]
  );

  useEffect(() => {
    if (editorContent && !isGenerationLoadingContent(editorContent)) {
      lastValidEditorContentRef.current = editorContent;
    }
  }, [editorContent]);

  useEffect(() => {
    if (titleTag?.trim()) {
      lastValidTitleRef.current = titleTag;
    }
  }, [titleTag]);

  useEffect(() => {
    if (metaDescriptionTag?.trim()) {
      lastValidMetaRef.current = metaDescriptionTag;
    }
  }, [metaDescriptionTag]);

  const [isReturningFromAgentEdit, setIsReturningFromAgentEdit] = useState(false);
  const hasRestoredFromRedux = useRef(false);
  const loadedTextIdRef = useRef(null);
  const pendingWebsiteIdRef = useRef(null);
  const websitesListRef = useRef(websitesList);
  websitesListRef.current = websitesList;

  const navigateToAgentEdit = (agentId, popinForm) => {
    try {
      sessionStorage.setItem(SETTINGS_POPIN_OPEN_KEY, 'true');
      if (popinForm) {
        const { knowledgeBaseFiles, files, ...serializableForm } = popinForm;
        const draftForm = {
          ...serializableForm,
          ...(Array.isArray(popinForm.keptKnowledgeBaseFileIds)
            ? {
              knowledgeBaseFiles: (Array.isArray(textKnowledgeBaseFiles)
                ? textKnowledgeBaseFiles
                : []
              ).filter((file) =>
                popinForm.keptKnowledgeBaseFileIds
                  .map((id) => String(id))
                  .includes(String(file?.id || ''))
              ),
            }
            : Array.isArray(textKnowledgeBaseFiles)
              ? { knowledgeBaseFiles: textKnowledgeBaseFiles }
              : {}),
        };
        sessionStorage.setItem(
          SETTINGS_POPIN_DRAFT_KEY,
          JSON.stringify({ form: draftForm, timestamp: Date.now() })
        );
      }
    } catch { }
    const actualTextId = resolveActualTextId(currentTextId);

    if (!actualTextId && !textId) {
      console.log('💾 Sauvegarde dans Redux avant navigation (mode création)', {
        formData,
        agentSettings,
        editorContent,
        websites,
        selectedWebsite,
        selectedAgent,
      });

      dispatch(saveFormSnapshot({
        formData,
        agentSettings,
        editorContent,
        websites,
        selectedWebsite,
        selectedAgent,
        hasGeneratedOnce,
        showEditor,
        currentContentScore,
        currentTextId,
        contentType: contentConfig?.contentType || 'PRODUCT',
        list,
        keywords,
        combinedKeywords,
      }));

      setTimeout(() => {
        console.log('✅ Données sauvegardées dans Redux');
      }, 100);
    }

    sessionStorage.setItem('isNavigatingToAgentEdit', 'true');
    localStorage.setItem('agentId', String(agentId));
    localStorage.setItem('agentMissionType', 'Rédaction');

    history.push(agentEditPath);
  };

  const getTextFromHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  };

  const plainTextToHtml = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return '';
    if (trimmed.includes('<')) return trimmed;
    return `<p>${trimmed.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
  };

  const applyExistingTextAsInitial = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) {
      setInitialHtmlContent('');
      setInitialTextContent('');
      return;
    }
    const html = plainTextToHtml(trimmed);
    setInitialHtmlContent(html);
    setInitialTextContent(getTextFromHtml(html));
  };

  const handleInitialHtmlContentChange = (html) => {
    setInitialHtmlContent(html);
    setExistingText(getTextFromHtml(html));
  };

  const handleInitialTextContentChange = (text) => {
    setInitialTextContent(text);
    setExistingText(text);
  };

  const normalizeUrl = (input) => {
    return (input || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  };

  const parseWebsiteStorageValue = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    try {
      const parsed = JSON.parse(value);
      return parsed?.urlSite || parsed?.url || parsed?.name || value;
    } catch {
      return value;
    }
  };

  const resolveWebsiteUrl = (...sources) => {
    if (selectedWebsite?.urlSite) return selectedWebsite.urlSite;
    const candidates = [...sources, selectedWebsiteUrl];
    for (const raw of candidates) {
      const url = parseWebsiteStorageValue(raw);
      if (url) return url;
    }
    return '';
  };

  const resolveWebsiteRecord = (...sources) => {
    const websiteUrl = resolveWebsiteUrl(...sources);
    const normalized = normalizeUrl(websiteUrl);

    if (normalized && Array.isArray(websitesList)) {
      const fromList = websitesList.find((w) => {
        const wUrl = w.urlSite || w.url || w.name || '';
        return normalizeUrl(wUrl) === normalized;
      });
      if (fromList) return fromList;
    }

    if (selectedWebsite?.id && Array.isArray(websitesList)) {
      const fromId = websitesList.find(
        (w) => String(w.id) === String(selectedWebsite.id)
      );
      if (fromId) return fromId;
    }

    return selectedWebsite || null;
  };

  const resolvedWebsiteId = useMemo(() => {
    if (selectedWebsite?.id) return selectedWebsite.id;
    if (agentSettings?.websiteId) return agentSettings.websiteId;

    return resolveWebsiteRecord()?.id || null;
  }, [selectedWebsite, agentSettings?.websiteId, selectedWebsiteUrl, websitesList]);

  const {
    isPopinOpen: isAssignProjectPopinOpen,
    isSuccessModalOpen: isTextAssignedModalOpen,
    textPendingAssign,
    assignedProject,
    projects: assignableProjects,
    loadingProjects: isLoadingAssignableProjects,
    isSaving: isAssigningToProject,
    assignError,
    openAssignPopin,
    closeAssignPopin,
    closeSuccessModal: closeTextAssignedModal,
    handleAssign,
  } = useAssignTextToProject({
    websiteId: resolvedWebsiteId,
  });

  const formatWebsiteThematic = (item) => {
    if (item == null) return '';
    if (typeof item === 'string') return item.trim();
    if (typeof item === 'object') {
      return String(item.thematic || item.name || item.label || '').trim();
    }
    return '';
  };

  const resolveWebsiteThematicsService = (...sources) => {
    const website = resolveWebsiteRecord(...sources);
    const thematics = website?.thematics;
    if (!Array.isArray(thematics) || thematics.length === 0) return '';
    const labels = thematics.map(formatWebsiteThematic).filter(Boolean);
    return Array.from(new Set(labels)).join(',');
  };

  const resolvePayloadService = (overrides = {}) =>
    overrides.websiteThematicsService || resolveWebsiteThematicsService(overrides.website);

  const extractAgentLanguage = (agent) => {
    if (!agent) return '';
    const langue = agent.langue ?? agent.language;
    return resolveLanguageDisplayLabel(langue);
  };

  const findAgentById = (agentId, agentsList = redactionAgentsList) => {
    const id = String(agentId || '').trim();
    if (!id) return null;
    return (agentsList || []).find(
      (agent) => String(agent?.id || agent?.agentId || '') === id
    ) || null;
  };

  const resolveAgentLanguage = (overrides = {}) => {
    if (overrides.langue) return resolveLanguageDisplayLabel(overrides.langue);
    const fromSelected = extractAgentLanguage(selectedAgent);
    if (fromSelected) return fromSelected;

    const agentId =
      overrides.agentId !== undefined ? overrides.agentId : agentSettings.customAgent;
    const fromList = extractAgentLanguage(findAgentById(agentId));
    if (fromList) return fromList;

    return '';
  };

  const getSelectedLLM = (overrideLlm, { isEnrichment = false } = {}) => {
    let selected = 'gpt';
    if (overrideLlm) {
      if (overrideLlm === 'claude_gpt') {
        selected = 'both';
      } else {
        selected = overrideLlm;
      }
    } else if (agentSettings.useBoth) {
      selected = 'both';
    } else if (agentSettings.useClaude) {
      selected = 'claude';
    } else if (agentSettings.usePerplexity) {
      selected = 'perplexity';
    } else if (agentSettings.useGemini) {
      selected = 'gemini';
    } else if (agentSettings.usePremium) {
      selected = 'premium';
    }
    if (isEnrichment && !ENRICHMENT_ALLOWED_LLMS.has(selected === 'both' ? '' : selected)) {
      return 'claude';
    }
    return selected;
  };

  const formatGapItemsToWords = (items) =>
    normalizeSemanticGapList(items)
      .map((g) => {
        const word = (g?.word || '').toString().trim();
        const freq = Number(g?.frequency ?? 1);
        return word ? `${word}(${Number.isFinite(freq) && freq > 0 ? freq : 1})` : '';
      })
      .filter(Boolean);

  const getSemanticGapFromContentScore = (score) => {
    if (!score) return [];
    const fromRoot = score?.semanticResult?.data?.semanticGap;
    if (Array.isArray(fromRoot) && fromRoot.length > 0) return fromRoot;
    const fromMetrics = score?.metrics?.semanticResult?.data?.semanticGap;
    if (Array.isArray(fromMetrics) && fromMetrics.length > 0) return fromMetrics;
    return [];
  };

  const buildGapWords = (overrides = {}) => {
    const scoreSemanticGap =
      overrides.semanticGap !== undefined
        ? overrides.semanticGap
        : getSemanticGapFromContentScore(
          overrides.contentScore || currentContentScore || lastContentScoreRef.current
        );
    const fromScore = formatGapItemsToWords(scoreSemanticGap);
    if (fromScore.length > 0) return fromScore;

    // Fallback (1ère génération / saisie manuelle) : list / combinedKeywords (UI = clientFreqV2)
    const effectiveList = overrides.list !== undefined ? overrides.list : list;
    const effectiveCombinedKeywords =
      overrides.combinedKeywords !== undefined ? overrides.combinedKeywords : combinedKeywords;

    if (effectiveList && Array.isArray(effectiveList) && effectiveList.length > 0) {
      return formatGapItemsToWords(effectiveList);
    }

    if (effectiveCombinedKeywords && effectiveCombinedKeywords.trim()) {
      return formatGapItemsToWords(transformToSemanticGap(effectiveCombinedKeywords));
    }

    return [];
  };

  const buildYoutubePayload = (overrides = {}) => {
    const effectiveAgentId = overrides.agentId !== undefined ? overrides.agentId : agentSettings.customAgent;
    const effectiveWordsPerText =
      overrides.wordsPerText !== undefined ? overrides.wordsPerText : agentSettings.wordsPerText;
    const effectiveKeyword = overrides.keyword !== undefined ? overrides.keyword : keyword;
    const effectiveUrl = overrides.url !== undefined ? overrides.url : url;
    const effectiveContent = overrides.content !== undefined ? overrides.content : existingText;
    const effectiveRetranscription =
      overrides.retranscription !== undefined ? overrides.retranscription : retranscription;
    const effectiveWebsites = overrides.websites !== undefined ? overrides.websites : websites;
    const maillageInterne = (effectiveWebsites || '')
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    return {
      agentId: String(effectiveAgentId || ''),
      website: resolveWebsiteUrl(overrides.website),
      service: resolvePayloadService(overrides),
      originalText: String(effectiveContent || '').trim(),
      urlPage: String(effectiveUrl || ''),
      keyword: String(effectiveKeyword || ''),
      nombreMots: String(effectiveWordsPerText || 0),
      retranscription: String(effectiveRetranscription || '').trim(),
      gap: buildGapWords(overrides),
      maillageInterne,
    };
  };

  const resolveKeptKnowledgeBaseFileIds = (overrides = {}) => {
    if (Array.isArray(overrides.keptKnowledgeBaseFileIds)) {
      return overrides.keptKnowledgeBaseFileIds
        .map((id) => String(id || '').trim())
        .filter(Boolean);
    }
    if (Array.isArray(overrides.knowledgeBaseFileIds)) {
      return overrides.knowledgeBaseFileIds
        .map((id) => String(id || '').trim())
        .filter(Boolean);
    }
    return (Array.isArray(textKnowledgeBaseFiles) ? textKnowledgeBaseFiles : [])
      .map((file) => String(file?.id || file?.fileId || '').trim())
      .filter(Boolean);
  };

  const buildPayload = (overrides = {}) => {
    const gapWords = buildGapWords(overrides);
    const effectiveWebsites = overrides.websites !== undefined ? overrides.websites : websites;
    const effectiveWritingAxis = overrides.writingAxis !== undefined ? overrides.writingAxis : writingAxis;
    const effectiveAgentId = overrides.agentId !== undefined ? overrides.agentId : agentSettings.customAgent;
    const effectiveWordsPerText =
      overrides.wordsPerText !== undefined ? overrides.wordsPerText : agentSettings.wordsPerText;
    const effectiveKeyword = overrides.keyword !== undefined ? overrides.keyword : keyword;
    const effectiveUrl = overrides.url !== undefined ? overrides.url : url;
    const effectiveContent = overrides.content !== undefined ? overrides.content : existingText;
    const maillageInterne = (effectiveWebsites || '')
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const payload = {
      agentId: effectiveAgentId || '',
      nombreMots: String(effectiveWordsPerText || 0),
      keyword: effectiveKeyword,
      urlPage: effectiveUrl,
      website: resolveWebsiteUrl(overrides.website),
      service: resolvePayloadService(overrides),
      gap: gapWords.length > 0 ? gapWords : [],
      maillageInterne: maillageInterne.length > 0 ? maillageInterne : [],
      axeRedaction: effectiveWritingAxis || '',
    };

    const trimmedContent = String(effectiveContent || '').trim();
    if (contentConfig?.service === 'CONTENT_IMPROVEMENT' && trimmedContent) {
      payload.content = trimmedContent;
    }

    const hasExplicitKeptIds =
      Array.isArray(overrides.keptKnowledgeBaseFileIds) ||
      Array.isArray(overrides.knowledgeBaseFileIds);
    const keptKnowledgeBaseFileIds = resolveKeptKnowledgeBaseFileIds(overrides);
    if (hasExplicitKeptIds || keptKnowledgeBaseFileIds.length > 0) {
      payload.keptKnowledgeBaseFileIds = keptKnowledgeBaseFileIds;
    }

    return payload;
  };

  const getAIAgentModel = (overrideLlm) => {
    const llm = getSelectedLLM(overrideLlm);
    if (llm === 'claude') return 'CLAUDE';
    if (llm === 'gpt') return 'GPT';
    if (llm === 'both') return 'GPT + CLAUDE';
    if (llm === 'perplexity') return 'PERPLEXITY';
    if (llm === 'gemini') return 'GEMINI';
    if (llm === 'premium') return 'PREMIUM';
    return '';
  };

  const buildYoutubePersistedFields = () => {
    if (contentConfig?.service !== 'YOUTUBE') return {};
    return {
      retranscription: String(retranscription || '').trim(),
    };
  };

  const buildExternalPayload = () => {
    // Préparer le semanticGap
    const semanticGapValue = list && list.length > 0 ? list : (combinedKeywords ? transformToSemanticGap(combinedKeywords) : []);

    // Créer ou mettre à jour le contentScore avec le semanticGap
    let updatedContentScore = currentContentScore;
    if (semanticGapValue && semanticGapValue.length > 0) {
      if (!updatedContentScore) {
        updatedContentScore = {
          semanticResult: {
            data: {
              semanticGap: [],
              remainingKeywords: [],
              wordsInBold: []
            }
          }
        };
      } else {
        updatedContentScore = {
          ...updatedContentScore,
          semanticResult: {
            ...(updatedContentScore.semanticResult || {}),
            data: {
              ...(updatedContentScore.semanticResult?.data || {}),
              semanticGap: semanticGapValue
            }
          }
        };
      }
    }

    const isEnrichment = contentConfig?.service === 'CONTENT_IMPROVEMENT';
    const isYoutube = contentConfig?.service === 'YOUTUBE';
    const resolvedInitialText =
      String(initialTextContent || '').trim()
      || getTextFromHtml(initialHtmlContent).trim()
      || String(existingText || '').trim();
    const resolvedInitialHtml =
      String(initialHtmlContent || '').trim()
      || (resolvedInitialText ? plainTextToHtml(resolvedInitialText) : '');
    const hasInitialContent = !!(resolvedInitialText || resolvedInitialHtml);
    const shouldPreserveInitialContent = (isEnrichment || isYoutube) && hasInitialContent;

    return {
      keyword: keyword || '',
      url: url || '',
      titleTag: titleTag || '',
      metaDescriptionTag: metaDescriptionTag || '',
      websiteId: resolvedWebsiteId || selectedWebsite?.id || agentSettings?.websiteId || 0,
      contentScore: updatedContentScore,
      actualTextContent: getTextFromHtml(editorContent),
      actualHtmlContent: editorContent,
      initialTextContent: shouldPreserveInitialContent
        ? resolvedInitialText
        : getTextFromHtml(editorContent),
      initialHtmlContent: shouldPreserveInitialContent
        ? resolvedInitialHtml
        : editorContent,
      activeInitialText: isEnrichment || isYoutube ? hasInitialContent : true,
      type: 'NON_TRANSLATED',
      numberWords: Number(agentSettings.wordsPerText) || 0,
      semanticGap: semanticGapValue,
      internalLinking: websites || '',
      axeRedaction: writingAxis || '',
      aiAgentId: agentSettings.customAgent || '',
      aiAgentModel: getAIAgentModel(),
      contentType: contentConfig?.contentType || 'PRODUCT',
      ...buildYoutubePersistedFields(),
    };
  };



  const buildDeliverPayload = () => {
    // Préparer le semanticGap
    const semanticGapValue = list && list.length > 0 ? list : (combinedKeywords ? transformToSemanticGap(combinedKeywords) : []);

    let updatedContentScore = currentContentScore;
    if (semanticGapValue && semanticGapValue.length > 0) {
      if (!updatedContentScore) {
        updatedContentScore = {
          semanticResult: {
            data: {
              semanticGap: [],
              remainingKeywords: [],
              wordsInBold: []
            }
          }
        };
      } else {
        updatedContentScore = {
          ...updatedContentScore,
          semanticResult: {
            ...(updatedContentScore.semanticResult || {}),
            data: {
              ...(updatedContentScore.semanticResult?.data || {}),
              semanticGap: semanticGapValue
            }
          }
        };
      }
    }

    return {
      keyword: keyword || '',
      url: url || '',
      titleTag: titleTag || '',
      metaDescriptionTag: metaDescriptionTag || '',
      contentScore: updatedContentScore,
      actualTextContent: getTextFromHtml(editorContent),
      actualHtmlContent: removeStrongTags(editorContent),
      numberWords: Number(agentSettings.wordsPerText) || 0,

      internalLinking: websites || '',
      axeRedaction: writingAxis || '',
      ...buildYoutubePersistedFields(),
    };
  };

  const refreshCompanyQuotas = useCallback(async () => {
    const { data } = await axiosPrivate.get('/project/company-quotas');
    setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);
    setTranslationEnabled(data?.translationEnabled !== false);
    const totalCredits = Number(data?.totalCredits);
    const usedCredits = Number(data?.usedCredits);
    if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
      setRemainingCredits(Math.max(0, totalCredits - usedCredits));
    } else {
      const apiRemainingCredits = Number(data?.remainingCredits);
      setRemainingCredits(Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null);
    }
    const rawModels = Array.isArray(data?.availableAiModels) ? data.availableAiModels : [];
    const normalizedModels = rawModels
      .map((model) => {
        const id = normalizeLlmId(
          typeof model === 'string' ? model : (model?.id || model?.value || model?.name || '')
        );
        if (!id) return null;
        return {
          id,
          name: typeof model === 'object' && model?.name ? model.name : formatModelName(id),
        };
      })
      .filter(Boolean);
    setAvailableAiModels(
      Array.from(new Map(normalizedModels.map((item) => [item.id, item])).values())
    );
  }, []);

  const refreshCreditsWithDelay = useCallback(
    async (delayMs = 0) => {
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      await refreshCompanyQuotas();
    },
    [refreshCompanyQuotas]
  );

  const buildTranslationSavePayload = () => ({
    keyword: keyword || '',
    url: url || '',
    titleTag: titleTag || '',
    metaDescriptionTag: metaDescriptionTag || '',
    actualTextContent: getTextFromHtml(translatedContent),
    initialTextContent: getTextFromHtml(editorContent),
    actualHtmlContent: translatedContent,
    initialHtmlContent: editorContent,
    activeInitialText: true,
    type: 'TRANSLATED',
    numberWords: getTextFromHtml(translatedContent).split(/\s+/).filter((word) => word.length > 0).length,
    maxWords: 0,
    internalLinking: websites || '',
    aiAgentId: translationAgentId || '',
    aiAgentModel: 'GPT_4O',
    websiteId: resolvedWebsiteId || selectedWebsite?.id || 0,
  });

  const handleStartTranslation = async ({ targetLanguage, agentId }) => {
    if (remainingCredits !== null && remainingCredits <= 0) {
      setIsCreditsLimitModalOpen(true);
      return;
    }

    if (!getTextFromHtml(editorContent).trim()) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Le texte source est requis pour la traduction.'));
      setErrorSuccess(true);
      return;
    }

    setIsTranslatePopinOpen(false);
    setShowEditor(true);
    setIsTranslationMode(true);
    setIsTranslating(true);
    setTranslationTargetLanguage(targetLanguage);
    setTranslationAgentId(agentId || '');
    savedTranslationTextIdRef.current = null;
    setSavedTranslationTextId(null);
    setTranslationSaveModalMode('create');
    setTranslatedContent(buildTranslationLoadingHtml(t('Traduction en cours...')));

    try {
      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      const response = await axiosPrivate.post(
        '/smart-content/smart_content/traduction/generate_text?maxTokens=10000&chatGPTModel=GPT_4_1&temperature=0.3',
        {
          agentId: agentId || '',
          keyword: keyword || '',
          urlPage: url || '',
          originalText: editorContent,
        }
      );

      const translated =
        response.data?.output ??
        response.data?.translatedText ??
        response.data;
      setTranslatedContent(typeof translated === 'string' ? translated : '');

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    } catch (error) {
      console.error('Erreur lors de la traduction:', error);
      setTranslatedContent(`<p><em>${t('Une erreur est survenue lors de la traduction.')}</em></p>`);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSaveTranslation = async () => {
    if (isTranslationLoadingContent(translatedContent)) {
      return;
    }

    if (!getTextFromHtml(translatedContent).trim()) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Veuillez d\'abord traduire un texte avant de sauvegarder.'));
      setErrorSuccess(true);
      return;
    }

    if (!getTextFromHtml(editorContent).trim()) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Le texte source est requis pour la sauvegarde.'));
      setErrorSuccess(true);
      return;
    }

    setIsSavingTranslation(true);

    try {
      const payload = buildTranslationSavePayload();
      const existingId = savedTranslationTextIdRef.current;

      if (existingId) {
        await axiosPrivate.put(
          `/texts/external/unassigned/${existingId}`,
          payload
        );
        setTranslationSaveModalMode('update');
        setIsTranslationSaveModalOpen(true);
      } else {
        const response = await axiosPrivate.post('/texts/external/unassigned', payload);
        const newId = response.data?.id || response.data?.textId || response.data || null;
        const idStr = newId != null ? String(newId) : null;

        if (idStr && idStr !== '[object Object]') {
          savedTranslationTextIdRef.current = idStr;
          setSavedTranslationTextId(idStr);
        }

        setTranslationSaveModalMode('create');
        setIsTranslationSaveModalOpen(true);
      }

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la traduction:', error);
      setIsSaveErrorModalOpen(true);
    } finally {
      setIsSavingTranslation(false);
    }
  };

  const handleGoToSavedTranslation = () => {
    const textId = savedTranslationTextIdRef.current || localStorage.getItem('AdminClient_TextId');
    setIsTranslationSaveModalOpen(false);

    try {
      sessionStorage.setItem(NAVIGATING_TO_TRANSLATION_IA_KEY, 'true');
      if (textId) {
        localStorage.setItem('AdminClient_TextId', String(textId));
      }
    } catch { }

    history.push(
      isConsultantRoute
        ? '/Consultant/CreateTraductionIA'
        : (textId ? `/AdminClient/CreateTraductionIA/${textId}` : '/AdminClient/CreateTraductionIA')
    );
  };

  const handleStayOnTranslation = () => {
    setIsTranslationSaveModalOpen(false);
  };

  const handleSave = async () => {
    setSaveError('');
    setSaveSuccess(false);
    setIsSaving(true);
    try {
      const payload = buildExternalPayload();

      if (currentTextId) {
        await axiosPrivate.put(`/texts/external/unassigned/${currentTextId}`, payload);
      } else {
        const createResponse = await axiosPrivate.post('/texts/external/unassigned', payload);
        const newId = createResponse?.data?.id || createResponse?.data?.textId || null;
        if (newId) {
          syncTextIdStorageAndUrl(newId);
        }
      }

      setSaveSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte enregistré avec succès'));
      setErrorSuccess(true);

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      dispatch(clearFormSnapshot());
      hasRestoredFromRedux.current = false;

      setTimeout(() => {
        setSaveSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du texte:', error);
      setSaveError(error?.response?.data?.message || error.message || t('Erreur lors de l\'enregistrement'));
      setIsSaveErrorModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const reloadTextFullDetails = useCallback(async (textId) => {
    if (!textId) return null;
    try {
      const response = await axiosPrivate.get(
        `/texts/external/unassigned/${textId}/fullDetails`
      );
      const data = response.data;
      if (!data) return null;

      setFormData({
        keyword: data.keyword || '',
        url: data.url || data.urlPage || '',
        titleTag: data.titleTag || '',
        metaDescriptionTag: data.metaDescriptionTag || '',
      });

      setAiAgent(data.aiAgentModel || '');
      setAgentSettings((prev) => ({
        ...prev,
        ...getAgentSettingsFlagsFromAgentModel(data.aiAgentModel),
        ...(data.numberWords
          ? { wordsPerText: Number(data.numberWords) || 0 }
          : {}),
        ...(data.aiAgentId ? { customAgent: data.aiAgentId } : {}),
        ...(data.websiteId ? { websiteId: data.websiteId } : {}),
      }));

      if (data.actualHtmlContent || data.actualTextContent) {
        const loadedHtml =
          data.actualHtmlContent ||
          (data.actualTextContent ? plainTextToHtml(data.actualTextContent) : '');
        if (loadedHtml.trim()) {
          setEditorContent(loadedHtml);
          setShowEditor(true);
          setHasGeneratedOnce(true);
        }
      }

      if (data.initialHtmlContent || data.initialTextContent) {
        const loadedInitialHtml =
          data.initialHtmlContent || plainTextToHtml(data.initialTextContent || '');
        setInitialHtmlContent(loadedInitialHtml);
        setInitialTextContent(
          data.initialTextContent || getTextFromHtml(loadedInitialHtml)
        );
        setExistingText(
          data.initialTextContent || getTextFromHtml(loadedInitialHtml)
        );
      }

      if (data.internalLinking !== undefined) {
        setWebsites(data.internalLinking || '');
      }

      if (data.retranscription !== undefined) {
        setRetranscription(data.retranscription);
      }

      if (data.axeRedaction !== undefined || data.writingAxis !== undefined) {
        setWritingAxis(data.axeRedaction || data.writingAxis || '');
      }

      if (Array.isArray(data.knowledgeBaseFiles)) {
        setTextKnowledgeBaseFiles(data.knowledgeBaseFiles);
      }

      const gapFromScore = extractSemanticGapFromFullDetails(data);
      if (gapFromScore.length > 0) {
        applySemanticGapState(gapFromScore);
      }

      if (data.contentScore && data.contentScore.globalScore > 0) {
        setCurrentContentScore(data.contentScore);
        lastContentScoreRef.current = data.contentScore;
      }

      return data;
    } catch (error) {
      console.error('Erreur lors du rechargement des full details:', error);
      return null;
    }
  }, [applySemanticGapState]);

  const handleDeliver = async () => {
    if (!currentTextId) return;
    setDeliverError('');
    setDeliverSuccess(false);
    setIsDelivering(true);
    try {
      const payload = buildDeliverPayload();
      await axiosPrivate.put(`/texts/external/unassigned/${currentTextId}/deliver`, payload);
      await reloadTextFullDetails(currentTextId);
      setDeliverSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte livré avec succès'));
      setErrorSuccess(true);

      dispatch(clearFormSnapshot());
      hasRestoredFromRedux.current = false;

      setTimeout(() => {
        setDeliverSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la livraison du texte:', error);
      setDeliverError(error?.response?.data?.message || error.message || t("Erreur lors de la livraison"));
    } finally {
      setIsDelivering(false);
    }
  };

  const handleValidate = async () => {
    if (!currentTextId) return;
    setValidateError('');
    setValidateSuccess(false);
    setIsValidating(true);
    try {
      const payload = {
        actualTextContent: getTextFromHtml(editorContent),
        actualHtmlContent: removeStrongTags(editorContent),
      };
      await axiosPrivate.put(`/texts/external/unassigned/${currentTextId}/validate`, payload);
      await reloadTextFullDetails(currentTextId);
      setValidateSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte validé avec succès'));
      setErrorSuccess(true);

      dispatch(clearFormSnapshot());
      hasRestoredFromRedux.current = false;

      setTimeout(() => {
        setValidateSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la validation du texte:', error);
      setValidateError(error?.response?.data?.message || error.message || t("Erreur lors de la validation"));
    } finally {
      setIsValidating(false);
    }
  };

  const handleGoOnline = async () => {
    if (!currentTextId || isGoingOnline) return;

    setActionsOpen(false);
    setHeaderActionsOpen(false);
    setDeployError('');
    setDeploySuccess(false);
    setIsGoingOnline(true);
    try {
      const payload = {
        textId: String(currentTextId),
        deployed: true,
        url: url || '',
      };
      await axiosPrivate.post('/texts/external/unassigned/deploy-new-url', payload);
      setDeploySuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte mis en ligne avec succès'));
      setErrorSuccess(true);

      dispatch(clearFormSnapshot());
      hasRestoredFromRedux.current = false;

      setTimeout(() => {
        setDeploySuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la mise en ligne du texte:', error);
      setDeployError(
        error?.response?.data?.message || error.message || t('Erreur lors de la mise en ligne')
      );
    } finally {
      setIsGoingOnline(false);
    }
  };

  const handleWordPressDeploy = async () => {
    if (!currentTextId || isDeploying) return;

    setActionsOpen(false);
    setHeaderActionsOpen(false);
    setDeployError('');
    setDeploySuccess(false);
    setWordPressDeployContent(editorContent || '');
    setShowWordPressDeployModal(true);
  };

  const handleWordPressDeploySuccess = async () => {
    if (!currentTextId) return;

    setIsDeploying(true);
    try {
      setDeploySuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Texte déployé avec succès sur WordPress'));
      setErrorSuccess(true);

      dispatch(clearFormSnapshot());
      hasRestoredFromRedux.current = false;

      setTimeout(() => {
        setDeploySuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur après déploiement WordPress:', error);
      setDeployError(
        error?.response?.data?.message ||
        error.message ||
        t('Erreur lors du déploiement')
      );
    } finally {
      setIsDeploying(false);
    }
  };

  const handleSendToRevision = async () => {
    if (!currentTextId || isSendingToRevision) return;
    setActionsOpen(false);
    setHeaderActionsOpen(false);
    setRevisionError('');
    setRevisionSuccess(false);
    setIsSendingToRevision(true);
    try {
      await axiosPrivate.put(
        `/texts/external/${currentTextId}/update-status?textStatusType=${encodeURIComponent('IN_REVISION')}`
      );
      setRevisionSuccess(true);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Le texte a été mis en révision avec succès.'));
      setErrorSuccess(true);

      dispatch(clearFormSnapshot());
      hasRestoredFromRedux.current = false;

      setTimeout(() => {
        setRevisionSuccess(false);
        setErrorSuccess(false);
      }, 3000);
    } catch (error) {
      if (isTextRevisionNotAllowedError(error)) {
        setIsRevisionErrorModalOpen(true);
      } else {
        console.error('Erreur lors de la mise en révision:', error);
        setRevisionError(
          error?.response?.data?.message || error.message || t('Erreur lors de la mise en révision')
        );
      }
    } finally {
      setIsSendingToRevision(false);
    }
  };

  const isEnrichmentRequest = () => contentConfig?.service === 'CONTENT_IMPROVEMENT';
  const isYoutubeRequest = () => contentConfig?.service === 'YOUTUBE';

  const generateYoutubeText = async (overrides = {}) => {
    const payload = buildYoutubePayload(overrides);

    try {
      setGenerationError('');
      const response = await axiosPrivate.post(buildYoutubeApiUrl(), payload);
      let generatedText =
        response.data?.output ||

        response.data?.description ||
        '';

      if (!generatedText && response.data && typeof response.data === 'string') {
        generatedText = response.data;
      }

      if (!generatedText || String(generatedText).trim() === '') {
        throw new Error('Aucun contenu généré');
      }

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      return formatGeneratedTextToHtml(generatedText);
    } catch (error) {
      console.error('Erreur lors de la génération YouTube:', error);
      console.error('Détails de l\'erreur:', error.response?.data);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        t('Erreur lors de la génération YouTube');
      setGenerationError(errorMsg);
      throw error;
    }
  };

  const generateText = async (overrideLlm, overrides = {}, genOptions = {}) => {
    const {
      generateTitleAndMeta = true,
      onJobAccepted,
      isRelaunch = false,
    } = genOptions;
    const isEnrichment = isEnrichmentRequest(overrides);
    const llm = getSelectedLLM(overrideLlm, { isEnrichment });
    const basePayload = buildPayload(overrides);
    const typeGeneration = resolveTypeGeneration(
      isEnrichment || contentConfig?.service
    );
    const websiteId =
      overrides.websiteId ||
      resolvedWebsiteId ||
      selectedWebsite?.id ||
      agentSettings?.websiteId;

    if (!websiteId) {
      const errorMsg = t('Veuillez sélectionner un site web');
      setGenerationError(errorMsg);
      throw new Error(errorMsg);
    }

    const payload = {
      ...buildUnassignedGenerationPayload({
        ...basePayload,
        llm: overrideLlm || llm,
        website: basePayload.website,
        typeGeneration,
        contentType:
          overrides.contentType ||
          contentConfig?.contentType ||
          contentConfig?.service ||
          '',
      }),
      ...(basePayload.content ? { content: basePayload.content } : {}),
      ...(Array.isArray(basePayload.keptKnowledgeBaseFileIds)
        ? { keptKnowledgeBaseFileIds: basePayload.keptKnowledgeBaseFileIds }
        : {}),
    };
    const files =
      overrides.files ||
      overrides.knowledgeBaseFiles ||
      [];

    try {
      setGenerationError('');
      const effectiveTextId =
        resolveActualTextId(currentTextId) || overrides.textId || null;
      const shouldUsePut = Boolean(effectiveTextId);

      if (isRelaunch && !effectiveTextId) {
        throw new Error('textId manquant pour la régénération');
      }

      const { html, textId: generatedTextId } = await runUnassignedGenerationJob({
        httpClient: axiosPrivate,
        websiteId,
        payload,
        files,
        website: payload.website,
        typeGeneration: payload.typeGeneration,
        textId: effectiveTextId,
        isRelaunch: shouldUsePut,
        onJobAccepted,
      });

      const resolvedTextId = generatedTextId || effectiveTextId || null;
      if (resolvedTextId) {
        syncTextIdStorageAndUrl(resolvedTextId);
      }

      let resolvedHtml = html;
      let title = '';
      let meta = '';

      const shouldRefreshDetailsForKb =
        Array.isArray(overrides.keptKnowledgeBaseFileIds) || files.length > 0;
      if (
        resolvedTextId &&
        (generateTitleAndMeta || !String(html || '').trim() || shouldRefreshDetailsForKb)
      ) {
        try {
          const detailsResponse = await axiosPrivate.get(
            `/texts/external/unassigned/${resolvedTextId}/fullDetails`
          );
          const details = detailsResponse?.data;
          if (!String(resolvedHtml || '').trim()) {
            const fromDetails =
              details?.actualHtmlContent ||
              (details?.actualTextContent
                ? formatGeneratedTextToHtml(details.actualTextContent)
                : '');
            if (fromDetails) resolvedHtml = fromDetails;
          }
          if (generateTitleAndMeta) {
            title = String(details?.titleTag || '').trim();
            meta = String(details?.metaDescriptionTag || '').trim();
          }
          if (Array.isArray(details?.knowledgeBaseFiles)) {
            setTextKnowledgeBaseFiles(details.knowledgeBaseFiles);
          } else if (
            shouldRefreshDetailsForKb &&
            Array.isArray(overrides.keptKnowledgeBaseFileIds)
          ) {
            const keptIds = new Set(
              overrides.keptKnowledgeBaseFileIds.map((id) => String(id))
            );
            setTextKnowledgeBaseFiles((prev) =>
              (Array.isArray(prev) ? prev : []).filter((file) =>
                keptIds.has(String(file?.id || ''))
              )
            );
          }
        } catch {
          // ignore details refresh errors
        }
      }

      if (!resolvedHtml || String(resolvedHtml).trim() === '') {
        throw new Error('Aucun contenu généré');
      }

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      return {
        html: resolvedHtml,
        title,
        meta,
      };
    } catch (error) {
      console.error(
        `Erreur lors de la génération avec ${llmIdToGenerationApiValue(overrideLlm || llm)}:`,
        error
      );
      console.error('Détails de l\'erreur:', error.response?.data);
      if (isPaymentRequiredError(error)) {
        setIsCreditsLimitModalOpen(true);
        setGenerationError('');
      } else {
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          t('Erreur lors de la génération');
        setGenerationError(errorMsg);
      }
      throw error;
    }
  };

  const generateTextStreaming = async (overrides = {}, streamOptions = {}) => {
    const {
      updateTitleAndMeta = true,
      updateContent = true,
      updateTitle = updateTitleAndMeta,
      updateMeta = updateTitleAndMeta,
      requireContent = true,
      generateTitleAndMeta = updateTitleAndMeta,
    } = streamOptions;
    const { keptKnowledgeBaseFileIds, ...basePayload } = buildPayload(overrides);
    const payload = {
      ...basePayload,
      generateTitleAndMeta,
      ...(Array.isArray(keptKnowledgeBaseFileIds)
        ? { knowledgeBaseFileIds: keptKnowledgeBaseFileIds }
        : {}),
    };
    const iaHeaders = buildIaHeadersForRedactionText('gpt');
    const streamUrl = buildGptStreamUrl();

    let sseBuffer = '';
    let lastResponseLength = 0;

    let accumulatedContent = '';
    let accumulatedTitle = '';
    let accumulatedMeta = '';

    const contentQueue = [];
    const titleQueue = [];
    const metaQueue = [];
    let displayedHtml = '';
    let displayedTitle = '';
    let displayedMeta = '';
    let typingTimer = null;
    const CHUNK_DELAY_MS = 45;

    const CURSOR =
      '<span style="display:inline-block;width:2px;height:1em;' +
      'background:#6458F1;margin-left:2px;vertical-align:text-bottom;' +
      'animation:blink 0.8s step-end infinite"></span>';

    const hasPendingChunks = () =>
      (updateContent && contentQueue.length > 0) ||
      (updateTitle && titleQueue.length > 0) ||
      (updateMeta && metaQueue.length > 0);

    const startTypingTimer = () => {
      if (typingTimer) return;
      typingTimer = setInterval(() => {
        if (updateContent && contentQueue.length > 0) {
          displayedHtml += contentQueue.shift();
          if (streamingDivRef.current) {
            streamingDivRef.current.innerHTML = displayedHtml + CURSOR;
          }
        }
        if (updateTitle && titleQueue.length > 0) {
          displayedTitle += titleQueue.shift();
          setFormData((prev) => ({ ...prev, titleTag: displayedTitle }));
        }
        if (updateMeta && metaQueue.length > 0) {
          displayedMeta += metaQueue.shift();
          setFormData((prev) => ({ ...prev, metaDescriptionTag: displayedMeta }));
        }
      }, CHUNK_DELAY_MS);
    };

    if (!document.getElementById('stream-cursor-style')) {
      const style = document.createElement('style');
      style.id = 'stream-cursor-style';
      style.textContent = '@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }';
      document.head.appendChild(style);
    }

    let firstContentChunk = true;

    const processSseLines = (lines) => {
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;

        const raw = trimmed.slice(5).trim();
        if (raw === '[DONE]') continue;

        try {
          const chunk = JSON.parse(raw);
          if (!chunk.content) continue;

          if (chunk.type === 'CONTENT' && updateContent) {
            if (firstContentChunk && streamingDivRef.current) {
              firstContentChunk = false;
              streamingDivRef.current.innerHTML = '';
            }
            accumulatedContent += chunk.content;
            contentQueue.push(chunk.content);
            startTypingTimer();
          } else if (chunk.type === 'TITLE' && updateTitle) {
            accumulatedTitle += chunk.content;
            titleQueue.push(chunk.content);
            startTypingTimer();
          } else if (chunk.type === 'META_DESCRIPTION' && updateMeta) {
            accumulatedMeta += chunk.content;
            metaQueue.push(chunk.content);
            startTypingTimer();
          }
        } catch {
          // malformed chunk — skip
        }
      }
    };

    const processIncomingSse = (incomingText) => {
      if (!incomingText) return;
      sseBuffer += incomingText;
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop() || '';
      processSseLines(lines);
    };

    try {
      const response = await axiosPrivate.post(streamUrl, payload, {
        headers: {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          ...iaHeaders,
        },
        responseType: 'text',
        onDownloadProgress: (progressEvent) => {
          const responseText = progressEvent?.event?.target?.responseText;
          if (typeof responseText !== 'string') return;
          const newPart = responseText.slice(lastResponseLength);
          lastResponseLength = responseText.length;
          processIncomingSse(newPart);
        },
      });

      const finalText = typeof response.data === 'string' ? response.data : '';
      if (finalText.length > lastResponseLength) {
        processIncomingSse(finalText.slice(lastResponseLength));
      }
      if (sseBuffer.trim()) {
        processSseLines([sseBuffer]);
        sseBuffer = '';
      }

      await new Promise((resolve) => {
        const drainCheck = setInterval(() => {
          if (!hasPendingChunks()) {
            clearInterval(drainCheck);
            if (typingTimer) clearInterval(typingTimer);
            resolve();
          }
        }, CHUNK_DELAY_MS);
      });

      if (updateContent && streamingDivRef.current) {
        streamingDivRef.current.innerHTML = displayedHtml;
      }

      if (updateTitle || updateMeta) {
        setFormData((prev) => ({
          ...prev,
          ...(updateTitle ? { titleTag: accumulatedTitle.trim() || prev.titleTag } : {}),
          ...(updateMeta
            ? { metaDescriptionTag: accumulatedMeta.trim() || prev.metaDescriptionTag }
            : {}),
        }));
      }

      if (requireContent && !accumulatedContent.trim()) throw new Error('Aucun contenu généré');
      if (updateTitle && !updateContent && !accumulatedTitle.trim()) {
        throw new Error('Aucun titre généré');
      }
      if (updateMeta && !updateContent && !accumulatedMeta.trim()) {
        throw new Error('Aucune meta description générée');
      }

      const finalHtml = accumulatedContent.includes('<')
        ? accumulatedContent
        : `<p>${accumulatedContent.replace(/\n/g, '</p><p>')}</p>`;

      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      return {
        html: finalHtml,
        title: accumulatedTitle.trim(),
        meta: accumulatedMeta.trim(),
      };
    } catch (error) {
      if (typingTimer) clearInterval(typingTimer);
      throw error;
    }
  };

  const getTitleAndMetaUrls = (kw, website, language, overrideLlm) => {
    const encodedKw = encodeURIComponent(kw || '');
    const encodedWebsite = encodeURIComponent(website || '');
    const encodedLanguage = encodeURIComponent(language || '');
    const llm = getSelectedLLM(overrideLlm);

    const openAiModel = getModelForProviderInApi('openai');
    const claudeModel = getModelForProviderInApi('claude');
    if (llm === 'claude') {
      return {
        titleUrl: `/smart-content/smart_content/generate_title/claude?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&claudeModel=${encodeURIComponent(claudeModel)}&temperature=0.3`,
        metaUrl: `/smart-content/smart_content/generate_meta_description/claude?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&claudeModel=${encodeURIComponent(claudeModel)}&temperature=0.3`
      };
    }
    return {
      titleUrl: `/smart-content/smart_content/generate_title/gpt?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&chatGPTModel=${encodeURIComponent(openAiModel)}&temperature=0.3`,
      metaUrl: `/smart-content/smart_content/generate_meta_description/gpt?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&chatGPTModel=${encodeURIComponent(openAiModel)}&temperature=0.3`
    };
  };

  const extractField = (res, candidates = []) => {
    try {
      const d = res?.data;
      if (typeof d === 'string') return d;
      for (const key of candidates) {
        if (d?.[key]) return d[key];
      }
      if (d?.data) {
        if (typeof d.data === 'string') return d.data;
        for (const key of candidates) {
          if (d.data?.[key]) return d.data[key];
        }
      }
    } catch (_) { }
    return '';
  };

  const getAgentLanguage = (overrides = {}) => resolveAgentLanguage(overrides);

  const canUseGptStreamForRegeneration = () => {
    const llm = getSelectedLLM();
    return llm === 'gpt' && !isEnrichmentRequest();
  };

  const handleRegenerateTitle = async () => {
    if (contentConfig?.service === 'GMB') return;
    if (!keyword.trim()) {
      setGenerationError(t('Veuillez entrer un mot-clé'));
      return;
    }
    if (!agentSettings.customAgent?.trim()) {
      setGenerationError(t('Veuillez sélectionner un agent IA'));
      return;
    }
    setIsRegeneratingTitle(true);
    setGenerationError('');
    setFormData((prev) => ({ ...prev, titleTag: '' }));
    try {
      if (canUseGptStreamForRegeneration()) {
        const { title } = await generateTextStreaming(
          {},
          {
            updateContent: false,
            updateTitle: true,
            updateMeta: false,
            generateTitleAndMeta: true,
            requireContent: false,
          }
        );
        if (title) {
          setFormData((prev) => ({ ...prev, titleTag: title }));
        }
      } else {
        const { titleUrl } = getTitleAndMetaUrls(
          keyword,
          resolveWebsiteUrl(),
          getAgentLanguage({ agentId: agentSettings.customAgent })
        );
        const iaHeaders = buildIaHeadersForTitleMeta(getSelectedLLM());
        const titleRes = await axiosSmartContent.post(titleUrl, {}, { headers: iaHeaders });
        const nextTitleRaw = extractField(titleRes, ['output', 'title', 'result', 'content']);
        const nextTitle = (nextTitleRaw || '').toString().trim();
        if (nextTitle) {
          setFormData((prev) => ({ ...prev, titleTag: nextTitle }));
        }
      }
    } catch (err) {
      console.error('Erreur lors de la régénération du titre:', err);
      if (isPaymentRequiredError(err)) {
        setIsCreditsLimitModalOpen(true);
      } else {
        setGenerationError(t('Erreur lors de la régénération du titre'));
      }
    } finally {
      setIsRegeneratingTitle(false);
      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    }
  };

  const handleRegenerateMeta = async () => {
    if (contentConfig?.service === 'GMB') return;
    if (!keyword.trim()) {
      setGenerationError(t('Veuillez entrer un mot-clé'));
      return;
    }
    if (!agentSettings.customAgent?.trim()) {
      setGenerationError(t('Veuillez sélectionner un agent IA'));
      return;
    }
    setIsRegeneratingMeta(true);
    setGenerationError('');
    setFormData((prev) => ({ ...prev, metaDescriptionTag: '' }));
    try {
      if (canUseGptStreamForRegeneration()) {
        const { meta } = await generateTextStreaming(
          {},
          {
            updateContent: false,
            updateTitle: false,
            updateMeta: true,
            generateTitleAndMeta: true,
            requireContent: false,
          }
        );
        if (meta) {
          setFormData((prev) => ({ ...prev, metaDescriptionTag: meta }));
        }
      } else {
        const { metaUrl } = getTitleAndMetaUrls(
          keyword,
          resolveWebsiteUrl(),
          getAgentLanguage({ agentId: agentSettings.customAgent })
        );
        const iaHeaders = buildIaHeadersForTitleMeta(getSelectedLLM());
        const metaRes = await axiosSmartContent.post(metaUrl, {}, { headers: iaHeaders });
        const nextMetaRaw = extractField(metaRes, ['output', 'metaDescription', 'result', 'content']);
        const nextMeta = (nextMetaRaw || '').toString().trim();
        if (nextMeta) {
          setFormData((prev) => ({ ...prev, metaDescriptionTag: nextMeta }));
        }
      }
    } catch (err) {
      console.error('Erreur lors de la régénération de la méta:', err);
      if (isPaymentRequiredError(err)) {
        setIsCreditsLimitModalOpen(true);
      } else {
        setGenerationError(t('Erreur lors de la régénération de la méta-description'));
      }
    } finally {
      setIsRegeneratingMeta(false);
      try {
        await refreshCompanyQuotas();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    }
  };

  const handleStartGeneration = async (overrideLlm, overrides = {}, options = {}) => {
    const effectiveAgentId = String(
      overrides.agentId !== undefined
        ? overrides.agentId
        : agentSettings.customAgent ||
        selectedAgent?.id ||
        selectedAgent?.agentId ||
        ''
    ).trim();

    const rawWords = Number(
      overrides.wordsPerText !== undefined
        ? overrides.wordsPerText
        : agentSettings.wordsPerText
    );
    const wordsFromEditor = getTextFromHtml(editorContent)
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;

    const effectiveWordsPerText =
      rawWords > 0 ? rawWords : wordsFromEditor > 0 ? wordsFromEditor : 600;

    const effectiveKeyword = String(
      overrides.keyword !== undefined ? overrides.keyword : keyword || ''
    ).trim();

    if (!effectiveAgentId || !effectiveKeyword) {
      if (isGenerationLoadingContent(editorContent)) {
        setIsGenerating(false);
        setShowEditor(false);
        setEditorContent('');
      }
      return;
    }

    const generationOverrides = {
      ...overrides,
      agentId: effectiveAgentId,
      wordsPerText: effectiveWordsPerText,
      keyword: effectiveKeyword,
    };

    setIsGenerating(true);
    setGenerationError('');

    const loadingHtml = buildGenerationLoadingHtml(
      t('Rédaction en cours...'),
      t('Génération en cours')
    );
    const previousEditorContent = isGenerationLoadingContent(editorContent)
      ? ''
      : editorContent;


    flushSync(() => setShowEditor(true));
    setEditorContent(loadingHtml);

    try {
      await refreshCompanyQuotas();
    } catch (quotaError) {
      console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
    }

    try {
      if (isYoutubeRequest()) {
        const generatedHtml = await generateYoutubeText(generationOverrides);
        setEditorContent(generatedHtml);
        setHasGeneratedOnce(true);

        try {
          await refreshCreditsWithDelay(500);
        } catch (quotaError) {
          console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
        }
        return;
      }

      const isEnrichment = isEnrichmentRequest(generationOverrides);
      const llm = getSelectedLLM(overrideLlm, { isEnrichment });
      console.log(
        `Génération en cours avec ${llmIdToGenerationApiValue(overrideLlm || llm)}...`
      );

      const hasKnowledgeBaseFiles = Boolean(
        (generationOverrides.files || generationOverrides.knowledgeBaseFiles || []).length
      );
      const useStreaming = llm === 'gpt' && !isEnrichment && !hasKnowledgeBaseFiles;
      const isRelaunch =
        hasContentOrGenerated || options.isRelaunch === true;

      const shouldGenerateTitleAndMeta =
        contentConfig?.service !== 'GMB' &&
        !isRelaunch &&
        options.generateTitleAndMeta !== false;

      if (useStreaming) {
        const showStreamingLoader = () => {
          if (streamingDivRef.current) {
            streamingDivRef.current.innerHTML = loadingHtml;
            return;
          }
          requestAnimationFrame(() => {
            if (streamingDivRef.current) {
              streamingDivRef.current.innerHTML = loadingHtml;
            }
          });
        };

        flushSync(() => {
          setShowEditor(true);
          setIsStreamingMode(true);
        });
        showStreamingLoader();

        try {
          const { html, title, meta } = await generateTextStreaming(generationOverrides, {
            updateContent: true,
            updateTitle: shouldGenerateTitleAndMeta,
            updateMeta: shouldGenerateTitleAndMeta,
            generateTitleAndMeta: shouldGenerateTitleAndMeta,
            requireContent: true,
          });

          setEditorContent(html);
          setIsStreamingMode(false);
          setHasGeneratedOnce(true);

          if (shouldGenerateTitleAndMeta) {
            if (title) {
              setFormData((prev) => ({ ...prev, titleTag: title }));
            }
            if (meta) {
              setFormData((prev) => ({ ...prev, metaDescriptionTag: meta }));
            }
          }

          try {
            await refreshCreditsWithDelay(500);
          } catch (quotaError) {
            console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
          }
        } catch (streamError) {
          console.error('Erreur streaming GPT:', streamError);
          setIsStreamingMode(false);
          if (isPaymentRequiredError(streamError)) {
            setIsCreditsLimitModalOpen(true);
            setGenerationError('');
            setEditorContent(previousEditorContent || '');
          } else {
            setEditorContent(`<p><em>${GENERATION_ERROR_MESSAGE}</em></p>`);
          }
        }
      } else {
        try {
          const generated = await generateText(overrideLlm, generationOverrides, {
            generateTitleAndMeta: shouldGenerateTitleAndMeta,
            isRelaunch,
          });
          setEditorContent(generated.html);
          setHasGeneratedOnce(true);

          if (shouldGenerateTitleAndMeta) {
            if (generated.title) {
              setFormData((prev) => ({ ...prev, titleTag: generated.title }));
            }
            if (generated.meta) {
              setFormData((prev) => ({ ...prev, metaDescriptionTag: generated.meta }));
            }
          }

          try {
            await refreshCreditsWithDelay(500);
          } catch (quotaError) {
            console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
          }
        } catch (error) {
          if (isPaymentRequiredError(error)) {
            setIsCreditsLimitModalOpen(true);
            setGenerationError('');
            setEditorContent(previousEditorContent || '');
          } else {
            const reasonMessage =
              error?.response?.data?.message ||
              error?.message ||
              '';
            if (reasonMessage) setGenerationError(reasonMessage);
            setEditorContent(
              previousEditorContent?.trim()
                ? previousEditorContent
                : `<p><em>${GENERATION_ERROR_MESSAGE}</em></p>`
            );
          }
        }
      }
    } catch (error) {
      console.error('Erreur inattendue lors de la génération:', error);
      setIsStreamingMode(false);
      if (isPaymentRequiredError(error)) {
        setIsCreditsLimitModalOpen(true);
        setGenerationError('');
        setEditorContent(previousEditorContent || '');
      } else {
        setEditorContent(`<p><em>${GENERATION_ERROR_MESSAGE}</em></p>`);
      }
    } finally {
      setIsGenerating(false);
      try {
        await refreshCreditsWithDelay(600);
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    }
  };

  useEffect(() => {
    if (hasAutoGeneratedFromPopin.current) return;
    try {
      const raw = window.localStorage.getItem(SMART_POPIN_CONTEXT_KEY);
      if (!raw) return;
      const ctx = JSON.parse(raw);
      if (!ctx || !ctx.form) return;

      if (ctx.type && ctx.type !== currentContentType) {
        return;
      }

      const briefTextId = ctx.meta?.textId;
      if (briefTextId) {
        syncTextIdStorageAndUrl(briefTextId);
      }

      const { form } = ctx;
      const restoredWebsiteUrl = form.website || ctx.meta?.websiteUrl || '';
      const restoredWebsiteId = ctx.meta?.websiteId;

      if (restoredWebsiteUrl || restoredWebsiteId) {
        setSelectedWebsite((prev) => ({
          ...(prev || {}),
          ...(restoredWebsiteId || prev?.id ? { id: restoredWebsiteId || prev?.id } : {}),
          urlSite: restoredWebsiteUrl || prev?.urlSite,
        }));
      }

      // Déduire le nombre de mots à partir du preset ou du champ personnalisé
      let words = form.presetWordCount ? Number(form.presetWordCount) : 0;
      if (!words && form.preset) {
        const presetMap = {
          ARTICLE_COURT_300: 300,
          ARTICLE_MOYEN_600: 600,
          ARTICLE_LONG_1000: 1000,
          YOUTUBE_350: 350,
          LINKEDIN_200: 200,
          X_150: 150,
        };
        words = presetMap[form.preset] || 0;
      }

      setFormData((prev) => ({
        ...prev,
        keyword: form.keyword || prev.keyword,
        url: form.targetUrl || prev.url,
      }));

      const restoredExistingText = (form.existingText ?? form.content ?? '').trim();
      setExistingText(restoredExistingText);
      applyExistingTextAsInitial(restoredExistingText);

      if (form.retranscription !== undefined) {
        setRetranscription(form.retranscription);
      }

      if (form.internalLinking) {
        setWebsites(form.internalLinking);
      }

      const restoredWritingAxis = form.writingAxis ?? form.axeRedaction;
      if (restoredWritingAxis !== undefined && restoredWritingAxis !== null) {
        setWritingAxis(String(restoredWritingAxis));
      }

      setAgentSettings((prev) => ({
        ...prev,
        customAgent: form.agentId || prev.customAgent,
        wordsPerText: words || prev.wordsPerText,
        ...(restoredWebsiteId ? { websiteId: restoredWebsiteId } : {}),
        ...getAgentSettingsFlagsFromLlm(form.llm, {
          isContentImprovement: contentConfig?.service === 'CONTENT_IMPROVEMENT',
        }),
      }));

      if (form.llm) {
        setAiAgent(getAIAgentModel(form.llm));
      }

      if (form.semanticGap) {
        try {
          applySemanticGapState(form.semanticGap);
        } catch { }
      }

      const pendingFiles = takePendingKnowledgeBaseFiles();

      popinAutoGenContextRef.current = {
        llm: form.llm,
        overrides: {
          website: form.website || ctx.meta?.websiteUrl || '',
          websiteThematicsService:
            form.websiteThematicsService
            || ctx.meta?.websiteThematicsService
            || '',
          langue: form.agentLangue || '',
          keyword: form.keyword || '',
          url: form.targetUrl || '',
          content: restoredExistingText || undefined,
          retranscription: form.retranscription || '',
          agentId: form.agentId || '',
          wordsPerText: words || 0,
          writingAxis: restoredWritingAxis !== undefined ? String(restoredWritingAxis || '') : undefined,
          websites: form.internalLinking || '',
          combinedKeywords: form.semanticGap ?? '',
          list:
            form.semanticGap !== undefined
              ? (form.semanticGap ? transformToSemanticGap(form.semanticGap) : [])
              : undefined,
          files: pendingFiles,
        },
      };

      hasAutoGeneratedFromPopin.current = true;
      window.localStorage.removeItem(SMART_POPIN_CONTEXT_KEY);

      flushSync(() => {
        setShowEditor(true);
        setIsGenerating(true);
        setEditorContent(
          buildGenerationLoadingHtml(
            t('Rédaction en cours...'),
            t('Génération en cours')
          )
        );
      });
      setShouldAutoStartFromPopin(true);
    } catch (e) {
      try {
        console.error('Erreur lors de la restauration du contexte SmartContent popin:', e);
      } catch { }
    }
  }, []);

  useEffect(() => {
    if (!shouldAutoStartFromPopin) return;
    if (!isCreditsResolved) return;

    const overrides = popinAutoGenContextRef.current?.overrides || {};
    const websiteUrl = resolveWebsiteUrl(overrides.website);

    if (!websiteUrl && websitesList.length === 0) return;

    const hasThematics = String(overrides.websiteThematicsService || '').trim();
    if (!hasThematics && websitesList.length === 0) return;
    if (!hasThematics && websitesList.length > 0 && popinAutoGenContextRef.current?.overrides) {
      popinAutoGenContextRef.current.overrides.websiteThematicsService = resolveWebsiteThematicsService(
        overrides.website
      );
    }

    setShouldAutoStartFromPopin(false);
    const popinContext = popinAutoGenContextRef.current;
    popinAutoGenContextRef.current = null;
    handleStartGeneration(popinContext?.llm, popinContext?.overrides || {}, {
      generateTitleAndMeta: true,
    });
  }, [shouldAutoStartFromPopin, isCreditsResolved, remainingCredits, websitesList, contentConfig]);

  const returnToCreateProject = () => setCreateIA(false);

  const handleGoBack = () => {
    // Check if we're navigating to agent edit
    const isNavigatingToAgentEdit = sessionStorage.getItem('isNavigatingToAgentEdit') === 'true';
    const isReturningToBrief =
      !isNavigatingToAgentEdit &&
      sessionStorage.getItem(BRIEF_FLOW_RETURN_KEY) === 'true';

    if (!isNavigatingToAgentEdit) {
      dispatch(clearFormSnapshot());
      hasRestoredFromRedux.current = false;
    }

    try {
      sessionStorage.removeItem('createSmartText_formSnapshot');
      sessionStorage.removeItem('isNavigatingToAgentEdit');
    } catch { }

    if (isReturningToBrief) {
      if (isConsultantRoute) {
        history.push('/Consultant/IA_Smart');
        return;
      }
      try {
        const textIdToKeep =
          currentTextId ||
          localStorage.getItem('AdminClient_TextId') ||
          sessionStorage.getItem(BRIEF_TEXT_ID_SESSION_KEY);
        if (textIdToKeep) {
          const idStr = String(textIdToKeep);
          localStorage.setItem('AdminClient_TextId', idStr);
          sessionStorage.setItem(BRIEF_TEXT_ID_SESSION_KEY, idStr);
          sessionStorage.setItem(RETURNING_TO_BRIEF_KEY, 'true');
          history.push(`/AdminClient/createIAbrief/${idStr}`);
          return;
        }
        sessionStorage.setItem(RETURNING_TO_BRIEF_KEY, 'true');
      } catch { }
      history.push('/AdminClient/createIAbrief');
      return;
    }

    if (!isNavigatingToAgentEdit) {
      try {
        localStorage.removeItem('AdminClient_TextId');
        sessionStorage.removeItem(BRIEF_FLOW_RETURN_KEY);
      } catch { }
    }

    navigateBackOrDashboard(history, homePath, location.state);
  };

  const saveFormToSession = () => {
    try {
      const formSnapshot = {
        formData,
        agentSettings,
        editorContent,
        websites,
        selectedWebsite,
        selectedAgent,
        hasGeneratedOnce,
        showEditor,
        currentContentScore,
        timestamp: Date.now(),
      };
      sessionStorage.setItem('createSmartText_formSnapshot', JSON.stringify(formSnapshot));
    } catch (error) {
      try { console.warn('saveFormToSession failed', error); } catch { }
    }
  };

  const restoreFormFromSession = () => {
    try {
      const saved = sessionStorage.getItem('createSmartText_formSnapshot');
      if (!saved) return false;
      const snapshot = JSON.parse(saved);
      const maxAge = 30 * 60 * 1000; // 30 minutes
      if (Date.now() - snapshot.timestamp > maxAge) {
        sessionStorage.removeItem('createSmartText_formSnapshot');
        return false;
      }
      if (snapshot.formData) setFormData(snapshot.formData);
      if (snapshot.agentSettings) setAgentSettings(snapshot.agentSettings);
      if (snapshot.editorContent) setEditorContent(snapshot.editorContent);
      if (snapshot.websites) setWebsites(snapshot.websites);
      if (snapshot.selectedWebsite) setSelectedWebsite(snapshot.selectedWebsite);
      if (snapshot.selectedAgent) setSelectedAgent(snapshot.selectedAgent);
      if (snapshot.hasGeneratedOnce !== undefined) setHasGeneratedOnce(snapshot.hasGeneratedOnce);
      if (snapshot.showEditor !== undefined) setShowEditor(snapshot.showEditor);
      if (snapshot.currentContentScore) setCurrentContentScore(snapshot.currentContentScore);
      return true;
    } catch (error) {
      console.error('Erreur lors de la restauration du formulaire:', error);
      return false;
    }
  };

  useEffect(() => {
    if (hasRestoredFromRedux.current) return;

    const textIdFromStorage = localStorage.getItem('AdminClient_TextId');
    const actualTextId = textId || textIdFromStorage;

    if (!actualTextId && !textId) {

      const restoreTimeout = setTimeout(() => {
        if (reduxFormState && reduxFormState.timestamp) {
          const maxAge = 30 * 60 * 1000;
          const age = Date.now() - reduxFormState.timestamp;

          if (age < maxAge && age >= 0) {
            console.log('✅ Restauration depuis Redux (mode création)', {
              formData: reduxFormState.formData,
              agentSettings: reduxFormState.agentSettings,
              editorContent: reduxFormState.editorContent?.substring(0, 50) + '...',
              selectedWebsite: reduxFormState.selectedWebsite,
              selectedAgent: reduxFormState.selectedAgent,
            });

            hasRestoredFromRedux.current = true;

            if (reduxFormState.formData) {
              setFormData(reduxFormState.formData);
            }
            if (reduxFormState.agentSettings) {
              setAgentSettings(reduxFormState.agentSettings);
            }
            if (reduxFormState.editorContent) {
              setEditorContent(reduxFormState.editorContent);
            }
            if (reduxFormState.websites !== undefined) {
              setWebsites(reduxFormState.websites);
            }
            if (reduxFormState.selectedWebsite) {
              setSelectedWebsite(reduxFormState.selectedWebsite);
            }
            if (reduxFormState.selectedAgent) {
              setSelectedAgent(reduxFormState.selectedAgent);
            }
            if (reduxFormState.hasGeneratedOnce !== undefined) {
              setHasGeneratedOnce(reduxFormState.hasGeneratedOnce);
            }
            if (reduxFormState.showEditor !== undefined) {
              setShowEditor(reduxFormState.showEditor);
            }
            if (reduxFormState.currentContentScore) {
              setCurrentContentScore(reduxFormState.currentContentScore);
            }
            if (reduxFormState.currentTextId) {
              setCurrentTextId(reduxFormState.currentTextId);
            }
            if (reduxFormState.list) {
              setList(reduxFormState.list);
            }
            if (reduxFormState.keywords) {
              setKeywords(reduxFormState.keywords);
            }
            if (reduxFormState.combinedKeywords) {
              setCombinedKeywords(reduxFormState.combinedKeywords);
            }

            setIsReturningFromAgentEdit(true);
          }
        }
      }, 100);

      const isBriefFlow =
        sessionStorage.getItem(BRIEF_FLOW_RETURN_KEY) === 'true' ||
        sessionStorage.getItem(RETURNING_TO_BRIEF_KEY) === 'true' ||
        Boolean(sessionStorage.getItem(BRIEF_TEXT_ID_SESSION_KEY));
      if (!isBriefFlow) {
        try {
          localStorage.removeItem('AdminClient_TextId');
        } catch { }
      }

      return () => clearTimeout(restoreTimeout);
    }
  }, [reduxFormState, textId]);

  useEffect(() => {
    if (isReturningFromAgentEdit) {
      setIsReturningFromAgentEdit(false);
      return;
    }

    const textIdFromStorage = localStorage.getItem('AdminClient_TextId');
    const actualTextId = textId || textIdFromStorage;
    const abortController = new AbortController();

    const getLoadingHtml = () => {
      const redactionEnCoursText = t('Rédaction en cours...');
      const generationEnCoursText = t('Génération en cours');
      return `
      <div style="text-align: center; padding: 40px;">
        <img src="/Images/gifencours.gif" alt="${generationEnCoursText}" style="width: 200px; height: 200px; margin-bottom: 20px;" />
        <p style="color: #666; font-size: 16px; font-weight: 500;">${redactionEnCoursText}</p>
      </div>
    `;
    };

    const applyLoadedTextContent = (data) => {
      if (data.actualHtmlContent || data.actualTextContent) {
        const loadedHtml =
          data.actualHtmlContent ||
          (data.actualTextContent ? plainTextToHtml(data.actualTextContent) : '');
        if (loadedHtml.trim()) {
          setEditorContent(loadedHtml);
          setShowEditor(true);
          setHasGeneratedOnce(true);
          return true;
        }
      }
      return false;
    };

    const loadTextData = async () => {
      setCurrentTextId(actualTextId);

      if (!actualTextId) {
        loadedTextIdRef.current = null;
        pendingWebsiteIdRef.current = null;
        setIsTextDataLoaded(true);
        return;
      }

      // Already fetched this text — skip duplicate fullDetails calls when
      // unrelated deps (i18n, websites list, etc.) would otherwise re-trigger.
      if (loadedTextIdRef.current === actualTextId) {
        return;
      }

      loadedTextIdRef.current = actualTextId;
      pendingWebsiteIdRef.current = null;
      setIsTextDataLoaded(false);

      try {
        const response = await axiosPrivate.get(`/texts/external/unassigned/${actualTextId}/fullDetails`);
        if (abortController.signal.aborted) {
          return;
        }
        const data = response.data;

        setFormData({
          keyword: data.keyword || '',
          url: data.url || data.urlPage || '',
          titleTag: data.titleTag || '',
          metaDescriptionTag: data.metaDescriptionTag || '',
        });

        setAiAgent(data.aiAgentModel || '');
        setAgentSettings((prev) => ({
          ...prev,
          ...getAgentSettingsFlagsFromAgentModel(data.aiAgentModel),
        }));

        if (data.initialHtmlContent || data.initialTextContent) {
          const loadedInitialHtml = data.initialHtmlContent || plainTextToHtml(data.initialTextContent || '');
          setInitialHtmlContent(loadedInitialHtml);
          setInitialTextContent(data.initialTextContent || getTextFromHtml(loadedInitialHtml));
          setExistingText(data.initialTextContent || getTextFromHtml(loadedInitialHtml));
        }

        if (data.internalLinking) {
          setWebsites(data.internalLinking);
        }

        if (data.retranscription !== undefined) {
          setRetranscription(data.retranscription);
        }

        if (data.axeRedaction !== undefined || data.writingAxis !== undefined) {
          setWritingAxis(data.axeRedaction || data.writingAxis || '');
        }

        if (Array.isArray(data.knowledgeBaseFiles)) {
          setTextKnowledgeBaseFiles(data.knowledgeBaseFiles);
        } else {
          setTextKnowledgeBaseFiles([]);
        }

        const gapFromScore = extractSemanticGapFromFullDetails(data);
        if (gapFromScore.length > 0) {
          applySemanticGapState(gapFromScore);
        }

        if (data.numberWords) {
          setAgentSettings(prev => ({
            ...prev,
            wordsPerText: Number(data.numberWords) || 0,
          }));
        }

        if (data.aiAgentId) {
          setAgentSettings(prev => ({
            ...prev,
            customAgent: data.aiAgentId,
          }));
        }

        let websiteIdForStatus = data.websiteId || null;
        let websiteUrlForStatus = data.website || data.urlSite || '';

        if (data.websiteId) {
          pendingWebsiteIdRef.current = data.websiteId;
          setAgentSettings(prev => ({
            ...prev,
            websiteId: data.websiteId,
          }));

          const matchingWebsite = (websitesListRef.current || []).find(
            (w) => String(w.id) === String(data.websiteId)
          );
          if (matchingWebsite) {
            setSelectedWebsite(matchingWebsite);
            websiteUrlForStatus = matchingWebsite.urlSite || matchingWebsite.url || websiteUrlForStatus;
          }
        }

        if (data.contentScore && data.contentScore.globalScore > 0) {
          setCurrentContentScore(data.contentScore);
          lastContentScoreRef.current = data.contentScore;
        }

        const llmFromModel = getLlmIdFromAgentModel(data.aiAgentModel);
        const skipStatusPolling = llmFromModel === 'gpt';

        if (websiteIdForStatus && !skipStatusPolling) {
          try {
            const agentType = String(
              data.aiAgentTypeContenu ||
              data.typeContenu ||
              data.contentType ||
              contentConfig?.service ||
              ''
            ).toUpperCase();
            const typeGeneration = resolveTypeGeneration(
              agentType === 'CONTENT_IMPROVEMENT' ||
              agentType === 'ENRICHISSEMENT' ||
              agentType === 'OPTIMIZATION'
            );

            const { status } = await fetchUnassignedGenerationStatus({
              httpClient: axiosPrivate,
              websiteId: websiteIdForStatus,
              textId: actualTextId,
              website: websiteUrlForStatus,
              typeGeneration,
            });

            if (abortController.signal.aborted) return;

            if (status && isGenerationInProgress(status)) {
              setIsGenerating(true);
              setShowEditor(true);
              setEditorContent(getLoadingHtml());

              const watched = await watchUnassignedGenerationUntilDone({
                httpClient: axiosPrivate,
                websiteId: websiteIdForStatus,
                textId: actualTextId,
                website: websiteUrlForStatus,
                typeGeneration,
                signal: abortController.signal,
              });

              if (abortController.signal.aborted) return;

              if (watched?.html) {
                setEditorContent(watched.html);
                setHasGeneratedOnce(true);
              } else {
                const refreshed = await axiosPrivate.get(
                  `/texts/external/unassigned/${actualTextId}/fullDetails`
                );
                if (!abortController.signal.aborted) {
                  applyLoadedTextContent(refreshed?.data || {});
                }
              }
              setIsGenerating(false);
            } else {
              applyLoadedTextContent(data);
            }
          } catch (statusError) {
            if (abortController.signal.aborted) return;
            console.warn('Erreur lors du check job status:', statusError);
            if (isPaymentRequiredError(statusError)) {
              setIsCreditsLimitModalOpen(true);
            }
            setIsGenerating(false);
            applyLoadedTextContent(data);
          }
        } else {
          applyLoadedTextContent(data);
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        console.error('Erreur lors du chargement du texte:', error);
        if (loadedTextIdRef.current === actualTextId) {
          loadedTextIdRef.current = null;
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsTextDataLoaded(true);
        }
      }
    };

    loadTextData();

    return () => {
      abortController.abort();
    };

  }, [textId, isReturningFromAgentEdit, applySemanticGapState, contentConfig?.service]);

  useEffect(() => {
    const websiteId = pendingWebsiteIdRef.current;
    if (!websiteId || websitesList.length === 0) return;

    const matchingWebsite = websitesList.find(
      (w) => String(w.id) === String(websiteId)
    );
    if (matchingWebsite) {
      setSelectedWebsite(matchingWebsite);
    }
  }, [websitesList]);


  useEffect(() => {
    if (isConsultantRoute) return undefined;

    const textIdFromStorage = localStorage.getItem('AdminClient_TextId');
    const actualTextId = textId || textIdFromStorage;

    if (!actualTextId) return undefined;

    const timeoutId = setTimeout(() => {
      performAutoSave();
    }, 30000);

    return () => clearTimeout(timeoutId);
  }, [
    isConsultantRoute,
    editorContent,
    textId,
    performAutoSave,
    titleTag,
    metaDescriptionTag,
    url,
    currentContentScore,
    isGenerating,
    isStreamingMode,
    isRegeneratingTitle,
    isRegeneratingMeta,
  ]);

  useEffect(() => {
    if (saveSuccess || deliverSuccess || validateSuccess || deploySuccess) {
      try { sessionStorage.removeItem('createSmartText_formSnapshot'); } catch { }
    }
  }, [saveSuccess, deliverSuccess, validateSuccess, deploySuccess]);

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const response = await axiosPrivate.get('/project/website/all');
        if (response.status === 200) {
          setWebsitesList(response.data || []);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des websites:', error);
        setWebsitesList([]);
      }
    };

    fetchWebsites();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCompanyQuotas = async () => {
      try {
        if (!isMounted) return;
        await refreshCompanyQuotas();
      } catch {
        if (!isMounted) return;
        setGapAnalysisEnabled(false);
        setAvailableAiModels([]);
        setRemainingCredits(null);
      } finally {
        if (isMounted) {
          setIsCreditsResolved(true);
        }
      }
    };

    loadCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, [refreshCompanyQuotas]);

  useEffect(() => {
    if (currentTextId || selectedWebsite?.id || websitesList.length === 0) {
      return;
    }

    const urlToMatch = selectedWebsite?.urlSite || selectedWebsiteUrl;
    if (!urlToMatch) return;

    const normalizedSelected = normalizeUrl(urlToMatch);
    const matchingWebsite = websitesList.find((w) => {
      const wUrl = w.urlSite || w.url || w.name || '';
      return normalizeUrl(wUrl) === normalizedSelected;
    });

    if (matchingWebsite) {
      setSelectedWebsite(matchingWebsite);
    }
  }, [selectedWebsiteUrl, selectedWebsite?.id, selectedWebsite?.urlSite, websitesList, currentTextId]);


  useEffect(() => {
    return () => {
      const isNavigatingToAgentEdit = sessionStorage.getItem('isNavigatingToAgentEdit') === 'true';
      const isReturningToBrief =
        sessionStorage.getItem(RETURNING_TO_BRIEF_KEY) === 'true' ||
        sessionStorage.getItem(BRIEF_FLOW_RETURN_KEY) === 'true';
      const isNavigatingToTranslationIA =
        sessionStorage.getItem(NAVIGATING_TO_TRANSLATION_IA_KEY) === 'true';

      try {
        sessionStorage.removeItem('createSmartText_formSnapshot');

        if (!isNavigatingToAgentEdit) {
          sessionStorage.removeItem('isNavigatingToAgentEdit');
        }
      } catch { }

      if (!isNavigatingToAgentEdit && !isReturningToBrief && !isNavigatingToTranslationIA) {
        try {
          localStorage.removeItem('AdminClient_TextId');
        } catch { }
      }
    };
  }, []);

  useEffect(() => {
    const fetchRedactionAgents = async () => {
      const websiteId = selectedWebsite?.id || agentSettings?.websiteId;
      const websiteUrl = resolveWebsiteUrl();

      if (!websiteUrl) {
        setRedactionAgentsList([]);
        return;
      }

      setIsLoadingAgents(true);
      try {
        const agentType = contentConfig?.agentType || 'PRODUCT';
        const websiteParam = encodeURIComponent(websiteUrl);
        let agents = [];

        if (agentType === 'ALL') {
          const contentTypes = ['PRODUCT', 'LONG_CATEGORY', 'STORE_LOCATOR', 'GMB'];
          const requests = contentTypes.map((typeContenu) =>
            axiosPrivate.get(
              `/smart-content/agent_ia/redaction/by_website_content_type?websiteUrl=${websiteParam}&typeContenu=${encodeURIComponent(typeContenu)}`
            )
          );
          requests.push(
            axiosPrivate.get(
              `/smart-content/agent_ia/redaction/free_content_type?websiteUrl=${websiteParam}`
            )
          );

          const responses = await Promise.allSettled(requests);
          const merged = [];
          responses.forEach((res) => {
            if (res.status !== 'fulfilled') return;
            const items = Array.isArray(res.value?.data) ? res.value.data : [];
            merged.push(...items);
          });

          const uniqueMap = new Map();
          merged.forEach((agent) => {
            const key = String(agent?.id || agent?.agentId || '');
            if (!key) return;
            if (!uniqueMap.has(key)) uniqueMap.set(key, agent);
          });
          agents = Array.from(uniqueMap.values());
        } else {
          const agentsUrl =
            agentType === 'OTHER'
              ? `/smart-content/agent_ia/redaction/free_content_type?websiteUrl=${websiteParam}`
              : agentType === 'CONTENT_IMPROVEMENT'
                ? `/smart-content/agent_ia/redaction/all?websiteUrl=${websiteParam}`
                : `/smart-content/agent_ia/redaction/by_website_content_type?websiteUrl=${websiteParam}&typeContenu=${encodeURIComponent(agentType)}`;

          const response = await axiosPrivate.get(agentsUrl);
          agents = response.data || [];
        }

        setRedactionAgentsList(agents);

        if (agentSettings.customAgent) {
          const matchingAgent = findAgentById(agentSettings.customAgent, agents);
          if (matchingAgent) {
            setSelectedAgent(matchingAgent);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des agents IA:', error);
        setRedactionAgentsList([]);
      } finally {
        setIsLoadingAgents(false);
      }
    };

    fetchRedactionAgents();
  }, [selectedWebsite, selectedWebsiteUrl, textId, currentTextId, agentSettings.customAgent, agentSettings.websiteId]);

  const llmValue = agentSettings.useBoth
    ? 'both'
    : agentSettings.useClaude
      ? 'claude'
      : agentSettings.usePerplexity
        ? 'perplexity'
        : agentSettings.useGemini
          ? 'gemini'
          : agentSettings.usePremium
            ? 'premium'
            : 'gpt';

  // Update booleans based on selection
  const handleSelect = (next) => {
    if (next === 'both') {
      setAgentSettings((s) => ({ ...s, useBoth: true, useGpt: false, useClaude: false, usePerplexity: false, useGemini: false, usePremium: false }));
    } else if (next === 'gpt') {
      setAgentSettings((s) => ({ ...s, useGpt: true, useClaude: false, useBoth: false, usePerplexity: false, useGemini: false, usePremium: false }));
    } else if (next === 'claude') {
      setAgentSettings((s) => ({ ...s, useClaude: true, useGpt: false, useBoth: false, usePerplexity: false, useGemini: false, usePremium: false }));
    } else if (next === 'perplexity') {
      setAgentSettings((s) => ({ ...s, usePerplexity: true, useGpt: false, useClaude: false, useBoth: false, useGemini: false, usePremium: false }));
    } else if (next === 'gemini') {
      setAgentSettings((s) => ({ ...s, useGemini: true, useGpt: false, useClaude: false, useBoth: false, usePerplexity: false, usePremium: false }));
    } else if (next === 'premium') {
      setAgentSettings((s) => ({ ...s, usePremium: true, useGpt: false, useClaude: false, useBoth: false, usePerplexity: false, useGemini: false }));
    }
  };

  const hasContentOrGenerated =
    hasGeneratedOnce ||
    (getTextFromHtml(editorContent).trim().length > 0 &&
      !isGenerationLoadingContent(editorContent));
  const isPendingFirstGeneration =
    Boolean(currentTextId) &&
    isTextDataLoaded &&
    !hasContentOrGenerated &&
    !isGenerating;
  const isScoreFeatureEnabled = contentConfig?.showScore !== false;
  const showGenerationActions = isConsultantRoute
    ? false
    : hasContentOrGenerated
      ? true
      : !Boolean(currentTextId) && isTextDataLoaded;
  const showScoreSidebar =
    isScoreFeatureEnabled &&
    (isConsultantRoute
      ? hasContentOrGenerated || Boolean(currentTextId) || isGenerating
      : showGenerationActions || isGenerating);
  const isSidebarOpen = showScoreSidebar && !sidebarCollapsed;

  const isYoutubeContent = contentConfig?.service === 'YOUTUBE';

  const renderEditorActions = (inHeader = false) => {
    if (isConsultantRoute) return null;

    const isOpen = inHeader ? headerActionsOpen : actionsOpen;
    const toggleOpen = () => {
      if (inHeader) {
        setHeaderActionsOpen((prev) => !prev);
        return;
      }
      setActionsOpen((prev) => !prev);
    };
    const dropdownStyle = inHeader
      ? { top: '100%', marginTop: 8, right: 0 }
      : { bottom: '100%', marginBottom: 8, right: 0 };

    if (!currentTextId) {
      return (
        <button
          type="button"
          className="btn"
          onClick={isSaving ? undefined : handleSave}
          disabled={isSaving}
          style={{
            background: isSaving ? '#ccc' : '#6458F1',
            border: 'none',
            color: '#fff',
            borderRadius: '9999px',
            padding: '12px 18px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
          }}
        >
          {isSaving ? t('Enregistrement...') : saveSuccess ? t('Enregistré') : t('Enregistrer')}
        </button>
      );
    }

    return (
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          className="btn"
          onClick={toggleOpen}
          style={{
            background: '#6458F1',
            border: 'none',
            color: '#fff',
            borderRadius: '9999px',
            padding: '12px 50px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 14,
            boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
            textTransform: 'none',
          }}
        >
          {t('Actions')}
          <span style={{ opacity: 0.6 }}>|</span>
          <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} />
        </button>
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              ...dropdownStyle,
              background: '#fff',
              borderRadius: 12,
              boxShadow:
                '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
              zIndex: 20,

            }}
          >
            <div
              onClick={isSaving ? undefined : handleSave}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                background: isSaving ? '#f5f5f5' : '#8B82F7',
                color: isSaving ? '#666' : '#fff',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
              }}
            >
              {isSaving ? t('Enregistrement...') : saveSuccess ? t('Enregistré') : t('Enregistrer ')}
            </div>
            <div
              onClick={isDelivering ? undefined : handleDeliver}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #f0f0f0',
                background: isDelivering ? '#f5f5f5' : '#fff',
                cursor: isDelivering ? 'not-allowed' : 'pointer',
                opacity: isDelivering ? 0.7 : 1,
              }}
            >
              {isDelivering ? t('Livraison...') : deliverSuccess ? t('Livré') : t('Livrer')}
            </div>
            <div
              onClick={isSendingToRevision ? undefined : handleSendToRevision}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #f0f0f0',
                background: isSendingToRevision ? '#f5f5f5' : '#fff',
                cursor: isSendingToRevision ? 'not-allowed' : 'pointer',
                opacity: isSendingToRevision ? 0.7 : 1,
              }}
            >
              {isSendingToRevision ? t('Mise en révision...') : t('En révision')}
            </div>
            <div
              onClick={isValidating ? undefined : handleValidate}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #f0f0f0',
                background: isValidating ? '#f5f5f5' : '#fff',
                cursor: isValidating ? 'not-allowed' : 'pointer',
                opacity: isValidating ? 0.7 : 1,
              }}
            >
              {isValidating ? t('Validation...') : validateSuccess ? t('Validé') : t('Valider')}
            </div>
            <div
              onClick={isGoingOnline ? undefined : handleGoOnline}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #f0f0f0',
                background: isGoingOnline ? '#f5f5f5' : '#fff',
                cursor: isGoingOnline ? 'not-allowed' : 'pointer',
                opacity: isGoingOnline ? 0.7 : 1,
              }}
            >
              {isGoingOnline ? t('En ligne...') : deploySuccess ? t('En ligne') : t('En ligne')}
            </div>
            <div
              onClick={isDeploying ? undefined : handleWordPressDeploy}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #f0f0f0',
                background: isDeploying ? '#f5f5f5' : '#fff',
                cursor: isDeploying ? 'not-allowed' : 'pointer',
                opacity: isDeploying ? 0.7 : 1,
                ...(!resolvedWebsiteId
                  ? { borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }
                  : {}),
              }}
            >
              {isDeploying ? t('Déploiement...') : t('Déployer via WordPress')}
            </div>
            {resolvedWebsiteId && (
              <>
                <div
                  style={{
                    borderTop: '1px solid #f0f0f0',
                    margin: '4px 0',
                  }}
                />
                <div
                  onClick={() => {
                    setActionsOpen(false);
                    setHeaderActionsOpen(false);
                    openAssignPopin({
                      id: currentTextId,
                      title: keyword || titleTag || '',
                    });
                  }}
                  style={{
                    padding: '12px 16px',
                    background: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    borderBottomLeftRadius: 12,
                    borderBottomRightRadius: 12,
                  }}
                >
                  <img
                    src="/Images/lier_projet_icon.png"
                    alt=""
                    style={{ width: 16, height: 16, flexShrink: 0 }}
                  />
                  {t('Lier à un projet')}
                </div>
              </>
            )}
            {saveError && (
              <div style={{ padding: '8px 16px', color: '#c33' }}>{saveError}</div>
            )}
            {deliverError && (
              <div style={{ padding: '8px 16px', color: '#c33' }}>{deliverError}</div>
            )}
            {revisionError && (
              <div style={{ padding: '8px 16px', color: '#c33' }}>{revisionError}</div>
            )}
            {validateError && (
              <div style={{ padding: '8px 16px', color: '#c33' }}>{validateError}</div>
            )}
            {deployError && (
              <div style={{ padding: '8px 16px', color: '#c33' }}>{deployError}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleDownloadWord = useCallback(() => {
    if (!currentTextId) {
      setShowSaveBeforeDownloadModal(true);
      return;
    }
    DownloadOneTextWordType(currentTextId, user);
  }, [currentTextId, user]);

  const scoringEditorContent = (() => {
    if (isStreamingMode && streamingDivRef.current?.innerHTML) {
      const streamingHtml = stripStreamingCursor(streamingDivRef.current.innerHTML);
      if (streamingHtml.trim() && !isGenerationLoadingContent(streamingHtml)) {
        return streamingHtml;
      }
    }
    if (isGenerationLoadingContent(editorContent)) {
      return lastValidEditorContentRef.current;
    }
    return editorContent;
  })();
  const scoringTextContent = getTextFromHtml(scoringEditorContent);
  const hasValidScoringContent =
    scoringTextContent.trim().length > 0 &&
    !isGenerationLoadingContent(scoringEditorContent || '');
  const useMockScoreDuringGeneration = isGenerating && !hasValidScoringContent;

  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <div
          className="box-header-top"
          style={
            isYoutubeContent
              ? {
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }
              : undefined
          }
        >
          <img
            src="/Images/back-icon.png"
            alt={t('Retour')}
            className="me-3"
            style={{ cursor: 'pointer', width: 36, height: 36, flexShrink: 0 }}
            onClick={handleGoBack}
          />
          {isYoutubeContent && !createIA && showEditor && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                marginLeft: 'auto',
              }}
            >
              {renderEditorActions(true)}
            </div>
          )}
        </div>

        <div
          style={{
            marginRight: !isSidebarOpen ? 0 : sidebarWidth,
          }}
        >
          {createIA ? (
            <div className="box-wrapper">
              <CreateCustomAIAgent
                returnToCreateProject={returnToCreateProject}
                selectedWebsite={{ id: 0, urlSite: '' }}
              />
            </div>
          ) : (
            <>
              <TextDetailsIA
                t={t}
                expandMetaField
                formData={{ keyword, url, titleTag, metaDescriptionTag }}
                errors={{ titleError, metaDescriptionError }}
                isFieldDisabled={isFieldDisabled}
                handleChange={handleChange}
                handleBlur={handleBlur}
                websitesList={websitesList}
                selectedWebsite={selectedWebsite}
                setSelectedWebsite={setSelectedWebsite}
                showSeoControls={contentConfig?.showSeoControls !== false}
                showUrlControls={contentConfig?.showUrlControls !== false}
                onRegenerateTitle={
                  !isConsultantRoute && hasContentOrGenerated
                    ? handleRegenerateTitle
                    : undefined
                }
                onRegenerateMeta={
                  !isConsultantRoute && hasContentOrGenerated
                    ? handleRegenerateMeta
                    : undefined
                }
                isRegeneratingTitle={isRegeneratingTitle}
                isRegeneratingMeta={isRegeneratingMeta}
                onStartGeneration={
                  isConsultantRoute
                    ? undefined
                    : () =>
                      handleStartGeneration(
                        undefined,
                        {
                          ...(Array.isArray(textKnowledgeBaseFiles) &&
                            textKnowledgeBaseFiles.length > 0
                            ? {
                              keptKnowledgeBaseFileIds: textKnowledgeBaseFiles
                                .map((file) =>
                                  String(file?.id || file?.fileId || '').trim()
                                )
                                .filter(Boolean),
                            }
                            : {}),
                        },
                        {
                          isRelaunch: hasContentOrGenerated,
                          generateTitleAndMeta: !hasContentOrGenerated,
                        }
                      )
                }
                isGenerating={isGenerating}
                hasContentOrGenerated={hasContentOrGenerated}
                isTranslationEnabled={!isConsultantRoute && translationEnabled}
                showGenerationActions={showGenerationActions}
                onOpenTranslationPopin={
                  isConsultantRoute ? undefined : () => setIsTranslatePopinOpen(true)
                }
                onOpenGenerationSettings={
                  isConsultantRoute ? undefined : () => setIsSettingsPopinOpen(true)
                }
                isCreditsExhausted={false}
                onCreditsLimitClick={() => setIsCreditsLimitModalOpen(true)}
                remainingCredits={remainingCredits}
                showCreditsBadge={!isConsultantRoute}
                actionsInlineWithTitle={isYoutubeContent}
                sidebarCollapsed={sidebarCollapsed}
                hasScore={showScoreSidebar}
                showContentScoreVideo={contentConfig?.service !== 'GMB'}
                selectedAgent={selectedAgent}
                aiAgentModel={aiAgentModel}
                agentName={
                  selectedAgent
                    ? selectedAgent.name ||
                    selectedAgent.agentName ||
                    ''
                    : ''
                }
              />

              {isScoreFeatureEnabled && (
                <TextDisplaySidebar
                  collapsed={!showScoreSidebar || sidebarCollapsed}
                  setCollapsed={setSidebarCollapsed}
                  showToggle={showScoreSidebar}
                  isActive={showScoreSidebar}
                  animateWidth={false}
                  width={sidebarWidth}
                  setWidth={setSidebarWidth}
                  uiOnly={
                    !showEditor ||
                    !editorContent ||
                    useMockScoreDuringGeneration
                  }
                  analyseResult={null}
                  loading={false}
                  combinedKeywords={combinedKeywords}
                  keywords={keywords}
                  handleKeywordChange={handleKeywordChange}
                  isKeywordBoxReadOnly={isConsultantRoute}
                  orderContentType={'EDITORIAL'}
                  stuffedKeywords={[]}
                  initialStuffedKeywords={[]}
                  autoKeywordsGenerationLoading={false}
                  onGenerateKeywords={() => { }}
                  websites={websites}
                  actualHtmlContent={
                    useMockScoreDuringGeneration ? '' : scoringEditorContent
                  }
                  actualTextContent={
                    useMockScoreDuringGeneration ? '' : scoringTextContent
                  }
                  handleInputChange={(e) => setWebsites(e?.target?.value || '')}
                  userRole={isConsultantRoute ? ROLES.CONSULTANT : ROLES.ADMIN}
                  orderWebsite={selectedWebsite?.urlSite || ''}
                  country={selectedWebsite?.country || 'FR'}
                  keyword={keyword}
                  url={url}
                  t={t}
                  list={list}
                  language={selectedWebsite?.language || null}
                  maxWords={Number(agentSettings.wordsPerText) || 0}
                  wordCount={
                    useMockScoreDuringGeneration
                      ? 0
                      : Number(
                        scoringTextContent
                          .split(/\s+/)
                          .filter((word) => word.length > 0).length
                      )
                  }
                  keywordStuffingLoading={false}
                  checkIfAllLinesGreen={() => { }}
                  isDelivered={false}
                  renderAnalyseText={null}
                  initialContentScore={
                    useMockScoreDuringGeneration ? null : currentContentScore
                  }
                  mode="adminClient"
                  textId={currentTextId}
                  defaultScoreTab="global"
                  preCalculatedScoreData={
                    useMockScoreDuringGeneration ? null : currentContentScore
                  }
                  onScoreDataChange={handleScoreDataChange}
                  handleGapUpdate={handleGapUpdate}
                />
              )}

              <SmartContentPopin
                isOpen={isSettingsPopinOpen}
                onClose={() => {
                  setIsSettingsPopinOpen(false);
                  setPopinRestoreDraft(null);
                }}
                onNavigateToAgentEdit={
                  isConsultantRoute
                    ? undefined
                    : (agentId, popinForm) => {
                      navigateToAgentEdit(agentId, popinForm);
                    }
                }
                agents={redactionAgentsList}
                llmOptions={contentConfig?.service === 'CONTENT_IMPROVEMENT' ? ENRICHMENT_LLM_OPTIONS : availableAiModels}
                isTargetUrlRequired={contentConfig?.service === 'CONTENT_IMPROVEMENT'}
                disableGenerateButton={false}
                gapAnalysisEnabled={gapAnalysisEnabled === true}
                showAdvancedSeoSection={contentConfig?.service !== 'GMB' && contentConfig?.service !== 'YOUTUBE'}
                showWritingAxis={contentConfig?.service !== 'YOUTUBE'}
                contentType={currentContentType}
                showAgentContentTypeInDropdown={
                  contentConfig?.contentType === 'OTHER'
                  || contentConfig?.service === 'CONTENT_IMPROVEMENT'
                }
                onBlockedGenerateClick={() => {
                  setIsSettingsPopinOpen(false);
                  setIsCreditsLimitModalOpen(true);
                }}
                mode="edit"
                language={selectedWebsite?.language || null}
                country={selectedWebsite?.country || null}
                existingKnowledgeBaseFiles={
                  Array.isArray(textKnowledgeBaseFiles) ? textKnowledgeBaseFiles : undefined
                }
                initialValues={
                  popinRestoreDraft
                    ? {
                      ...popinRestoreDraft,
                      existingText:
                        popinRestoreDraft.existingText
                        ?? popinRestoreDraft.content
                        ?? '',
                      knowledgeBaseFiles: Array.isArray(popinRestoreDraft.knowledgeBaseFiles)
                        && popinRestoreDraft.knowledgeBaseFiles.length > 0
                        ? popinRestoreDraft.knowledgeBaseFiles
                        : (Array.isArray(textKnowledgeBaseFiles) ? textKnowledgeBaseFiles : []),
                    }
                    : {
                      keyword,
                      agentId: agentSettings.customAgent || '',
                      llm: getCurrentLlmForPopin(),
                      targetUrl: url || '',
                      existingText: existingText || '',
                      retranscription: retranscription || '',
                      semanticGap:
                        (combinedKeywords && String(combinedKeywords).trim()) ||
                        formatKeywordData(
                          extractSemanticGapFromFullDetails({
                            contentScore:
                              currentContentScore || lastContentScoreRef.current,
                          })
                        ) ||
                        '',
                      internalLinking: websites || '',
                      writingAxis: writingAxis || '',
                      knowledgeBaseFiles: Array.isArray(textKnowledgeBaseFiles)
                        ? textKnowledgeBaseFiles
                        : [],
                      ...getCurrentPresetForPopin(),
                    }
                }
                onSubmit={(form) => {
                  let words = form.presetWordCount ? Number(form.presetWordCount) : 0;
                  if (!words && form.preset) {
                    const presetMap = {
                      ARTICLE_COURT_300: 300,
                      ARTICLE_MOYEN_600: 600,
                      ARTICLE_LONG_1000: 1000,
                      YOUTUBE_350: 350,
                      LINKEDIN_200: 200,
                      X_150: 150,
                    };
                    words = presetMap[form.preset] || 0;
                  }

                  setFormData((prev) => ({
                    ...prev,
                    keyword: form.keyword || prev.keyword,
                    url: form.targetUrl || prev.url,
                  }));

                  const nextExistingText = (form.existingText ?? form.content ?? '').trim();
                  setExistingText(nextExistingText);
                  applyExistingTextAsInitial(nextExistingText);

                  if (form.retranscription !== undefined) {
                    setRetranscription(form.retranscription || '');
                  }

                  setAgentSettings((prev) => ({
                    ...prev,
                    customAgent: form.agentId || prev.customAgent,
                    wordsPerText: words || prev.wordsPerText,
                    ...getAgentSettingsFlagsFromLlm(form.llm, {
                      isContentImprovement: contentConfig?.service === 'CONTENT_IMPROVEMENT',
                    }),
                  }));

                  if (form.llm) {
                    setAiAgent(getAIAgentModel(form.llm));
                  }

                  if (form.internalLinking !== undefined) {
                    setWebsites(form.internalLinking || '');
                  }

                  const nextWritingAxis = form.writingAxis ?? form.axeRedaction;
                  if (nextWritingAxis !== undefined) {
                    setWritingAxis(nextWritingAxis || '');
                  }

                  if (form.semanticGap) {
                    try {
                      applySemanticGapState(form.semanticGap);
                    } catch { }
                  }

                  const effectiveKeyword = form.keyword || keyword || '';
                  const effectiveUrl = form.targetUrl || url || '';
                  const effectiveAgentId = form.agentId || agentSettings.customAgent || '';
                  const effectiveWords = words || agentSettings.wordsPerText || 0;
                  const effectiveWebsites =
                    form.internalLinking !== undefined ? (form.internalLinking || '') : websites;
                  const effectiveCombinedKeywords =
                    form.semanticGap !== undefined ? (form.semanticGap || '') : combinedKeywords;
                  let effectiveList = list;
                  if (form.semanticGap !== undefined) {
                    try {
                      effectiveList = form.semanticGap
                        ? transformToSemanticGap(form.semanticGap)
                        : [];
                    } catch {
                      effectiveList = [];
                    }
                  }

                  const matchingAgent = findAgentById(effectiveAgentId);
                  const nextNewKnowledgeBaseFiles = form.knowledgeBaseFiles || form.files || [];
                  const generationOverrides = {
                    writingAxis: nextWritingAxis !== undefined ? (nextWritingAxis || '') : writingAxis,
                    websites: effectiveWebsites,
                    keyword: effectiveKeyword,
                    url: effectiveUrl,
                    website: form.website || resolveWebsiteUrl(),
                    websiteThematicsService: resolveWebsiteThematicsService(
                      form.website || resolveWebsiteUrl()
                    ),
                    langue: form.agentLangue || extractAgentLanguage(matchingAgent),
                    content: nextExistingText || undefined,
                    retranscription:
                      form.retranscription !== undefined ? (form.retranscription || '') : retranscription,
                    agentId: effectiveAgentId,
                    wordsPerText: effectiveWords,
                    combinedKeywords: effectiveCombinedKeywords,
                    list: effectiveList,
                    files: nextNewKnowledgeBaseFiles,
                    ...(Array.isArray(form.keptKnowledgeBaseFileIds)
                      ? { keptKnowledgeBaseFileIds: form.keptKnowledgeBaseFileIds }
                      : Array.isArray(form.knowledgeBaseFileIds)
                        ? { keptKnowledgeBaseFileIds: form.knowledgeBaseFileIds }
                        : {}),
                  };

                  if (Array.isArray(form.keptKnowledgeBaseFileIds)) {
                    const keptIds = new Set(
                      form.keptKnowledgeBaseFileIds.map((id) => String(id))
                    );
                    setTextKnowledgeBaseFiles((prev) =>
                      (Array.isArray(prev) ? prev : []).filter((file) =>
                        keptIds.has(String(file?.id || ''))
                      )
                    );
                  }

                  setIsSettingsPopinOpen(false);
                  setPopinRestoreDraft(null);
                  if (matchingAgent) {
                    setSelectedAgent(matchingAgent);
                  }

                  handleStartGeneration(form.llm || undefined, generationOverrides, {
                    isRelaunch: hasContentOrGenerated,
                    generateTitleAndMeta: !hasContentOrGenerated,
                  });
                }}
              />

              <TranslateContentPopin
                isOpen={isTranslatePopinOpen}
                onClose={() => setIsTranslatePopinOpen(false)}
                websiteUrl={resolveWebsiteUrl()}
                onSubmit={handleStartTranslation}
              />

              {isPendingFirstGeneration && (
                <PendingGenerationEmptyState
                  t={t}
                  hasAxeRedaction={Boolean(String(writingAxis || '').trim())}
                  onConfigureGeneration={() => setIsSettingsPopinOpen(true)}
                  isCreditsExhausted={false}
                  onCreditsLimitClick={() => setIsCreditsLimitModalOpen(true)}
                />
              )}

              {contentConfig?.service === 'CONTENT_IMPROVEMENT' &&
                (initialHtmlContent?.trim() || initialTextContent?.trim()) && (
                  <div className="row mt-2">
                    <InitialTextSection
                      userRole={ROLES.ADMIN_CLIENT}
                      textForm={{ initialHtmlContent }}
                      textType="EDITORIAL"
                      url={url}
                      initialHtmlContent={initialHtmlContent}
                      initialTextContent={initialTextContent}
                      isTextFieldDisabled={false}
                      formData={{ activeInitialText: true }}
                      handleStepOneEditorInit={() => { }}
                      setInitialHtmlContent={handleInitialHtmlContentChange}
                      setInitialTextContent={handleInitialTextContentChange}
                      setInitialTextWordCount={setInitialTextWordCount}
                      t={t}
                      isNotTranslation
                      isSaveDisabled={false}
                      isTextSaved={false}
                      textId={currentTextId}
                      defaultExpanded={false}
                      expandOnUrl={false}
                    />
                  </div>
                )}

              {showEditor && isTranslationMode && (
                <SmartTextTranslationView
                  t={t}
                  sourceContent={editorContent}
                  translatedContent={translatedContent}
                  onTranslatedContentChange={setTranslatedContent}
                  sourceLanguageLabel={getAgentLanguage()}
                  targetLanguageLabel={translationTargetLanguage?.language || ''}
                  isTranslating={isTranslating}
                  isSavingTranslation={isSavingTranslation}
                  onSaveTranslation={handleSaveTranslation}
                  sourceTextId={currentTextId}
                  getTextFromHtml={getTextFromHtml}
                  renderSourceActions={() => renderEditorActions(false)}
                  readOnly={isConsultantRoute}
                />
              )}

              {showEditor && !isTranslationMode && (
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="box-wrapper-editor">
                      <div
                        ref={streamingDivRef}
                        style={{
                          display: isStreamingMode ? 'block' : 'none',
                          minHeight: 400,
                          padding: '16px',
                          background: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: 8,
                          fontFamily: 'inherit',
                          fontSize: 15,
                          lineHeight: 1.7,
                          overflowY: 'auto',
                        }}
                      />

                      <div style={{ display: isStreamingMode ? 'none' : 'block' }}>
                        <CustomEditor
                          t={t}
                          textId={currentTextId}
                          content={editorContent}
                          isReadOnly={isConsultantRoute}
                          allowConsultantEdit={false}
                          disabled={isGenerating}
                          onDownloadWord={handleDownloadWord}
                          downloadFileName={keyword || 'document'}
                          onInit={(evt, editor) => { }}
                          onChange={(value, editor) => {
                            if (isConsultantRoute) return;
                            setEditorContent(value);
                          }}
                        />
                      </div>
                    </div>


                    <div style={{ marginTop: 12, marginBottom: (actionsOpen || headerActionsOpen) ? 120 : 50, display: 'flex', justifyContent: 'flex-end' }}>
                      {renderEditorActions(false)}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <ErrorSuccesModal
          open={errorSuccess}
          message={errorSuccessMsg}
          handleClose={() => setErrorSuccess(false)}
          success={isSuccess}
          toggle={() => setErrorSuccess(!errorSuccess)}
        />
        <InsufficientCreditsModal
          isOpen={isCreditsLimitModalOpen}
          onClose={() => setIsCreditsLimitModalOpen(false)}
          onConfirm={() => {
            setIsCreditsLimitModalOpen(false);
            setIsBuyCreditsModalOpen(true);
          }}
        />
        <BuyCreditsModal
          open={isBuyCreditsModalOpen}
          onClose={() => setIsBuyCreditsModalOpen(false)}
        />
        <QuitProjectCreationModal
          isOpen={showSaveBeforeDownloadModal}
          onClose={() => setShowSaveBeforeDownloadModal(false)}
          title={t('Téléchargement')}
          message={t('Veuillez enregistrer le texte avant de pouvoir le télécharger.')}
          showSuggestion={false}
          showSecondaryButton={false}
          primaryButtonLabel={t('Fermer')}
          onPrimaryAction={() => setShowSaveBeforeDownloadModal(false)}
        />
        <QuitProjectCreationModal
          isOpen={isRevisionErrorModalOpen}
          onClose={() => setIsRevisionErrorModalOpen(false)}
          variant="enrichment"
          title={t('Mise en révision impossible')}
          message={t(
            'Ce texte est validé ou mis en ligne. Il ne peut pas être envoyé en révision.'
          )}
          primaryButtonLabel={t('Fermer')}
          onPrimaryAction={() => setIsRevisionErrorModalOpen(false)}
          showSecondaryButton={false}
          showSuggestion={false}
        />
        <QuitProjectCreationModal
          isOpen={isSaveErrorModalOpen}
          onClose={() => setIsSaveErrorModalOpen(false)}
          variant="enrichment"
          title={t("Ce texte existe déjà. Veuillez vérifier les autres sections de rédaction IA ou du projet.")}
          message={t("Ce texte existe déjà. Veuillez vérifier les autres sections de rédaction IA ou du projet.")}
          primaryButtonLabel={t('Fermer')}
          onPrimaryAction={() => setIsSaveErrorModalOpen(false)}
          showSecondaryButton={false}
          showSuggestion={false}
        />
        <TranslationSavedModal
          isOpen={isTranslationSaveModalOpen}
          onClose={handleStayOnTranslation}
          onGoToTranslation={handleGoToSavedTranslation}
          targetLanguageLabel={translationTargetLanguage?.language || ''}
          keyword={keyword || ''}
          mode={translationSaveModalMode}
        />
        <AssignTextToProjectPopin
          isOpen={isAssignProjectPopinOpen}
          onClose={closeAssignPopin}
          projects={assignableProjects}
          loadingProjects={isLoadingAssignableProjects}
          isSaving={isAssigningToProject}
          errorKey={assignError}
          onSave={handleAssign}
          onCreateProject={
            isConsultantRoute
              ? undefined
              : () => {
                closeAssignPopin();
                history.push('/AdminClient/CreateProject');
              }
          }
          keyword={textPendingAssign?.title || ''}
        />
        <TextAssignedToProjectModal
          isOpen={isTextAssignedModalOpen}
          onClose={closeTextAssignedModal}
          onGoToProjects={() => {
            closeTextAssignedModal();
            history.push(projectsPath);
          }}
          project={assignedProject}
        />
        <WordPressDeployModal
          isOpen={showWordPressDeployModal}
          onClose={() => setShowWordPressDeployModal(false)}
          textId={currentTextId}
          websiteId={resolvedWebsiteId}
          initialKeyword={keyword || ''}
          initialContent={wordPressDeployContent}
          onDeploySuccess={handleWordPressDeploySuccess}
        />
      </SemanticScoreProvider>
    </WordsProvider>
  );
}
